import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { buildInvoicePDF, printPDF } from "@/lib/invoicePDF";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Search, Plus, Minus, Trash2, PauseCircle, PlayCircle, Printer, RefreshCw,
  CreditCard, Banknote, Smartphone, HelpCircle, Wifi, WifiOff,
  User, UserPlus, X, Edit2, Maximize, Minimize, ShoppingCart,
  Percent, IndianRupee, FileText, CheckCircle2, ShieldAlert,
  Wallet, Layers, DollarSign, History, AlertCircle, Sparkles, Receipt,
  Store, RotateCcw, Clock, Menu, ChevronDown, Bell, Gift,
  Flame, TrendingDown, TrendingUp, Tag, Calendar, Package,
  Users, BarChart3, Boxes, Truck, Settings, Bookmark, Check,
  MoreHorizontal, Database, ArrowUpRight, Cloud, CloudOff,
  Camera, CameraOff, SwitchCamera
} from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import {
  openOfflineDB,
  cacheProductsCatalog,
  searchOfflineCatalog,
  getCachedCatalogMeta,
  queueOfflineOrder,
  getOfflineQueueStats,
  getOfflineOrders,
  syncPendingQueue,
  getDeviceId,
} from "@/lib/offlinePOS";

// Web-Audio Beep for Barcode Scans & Key Actions
function playBeep(freq = 1400, dur = 0.08) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "square";
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    o.connect(g);
    g.connect(ctx.destination);
    o.start(now);
    o.stop(now + dur + 0.01);
    setTimeout(() => ctx.close(), 150);
  } catch {
    /* silent audio fallback */
  }
}

// Inline SVG: Red Barcode Scanner Graphic
function BarcodeIcon({ className = "w-5 h-5 text-red-600" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5v14" />
      <path d="M6 5v14" />
      <path d="M10 5v14" />
      <path d="M13 5v14" />
      <path d="M17 5v14" />
      <path d="M21 5v14" />
      <line x1="2" y1="12" x2="22" y2="12" stroke="#dc2626" strokeWidth="2.5" />
    </svg>
  );
}

// Inline SVG: Empty Cart Illustration with Red Theme & Burst
function EmptyCartIllustration() {
  return (
    <div className="relative w-28 sm:w-36 h-28 sm:h-36 mx-auto flex items-center justify-center">
      <span className="absolute top-2 left-6 w-2 h-2 rounded-full bg-red-400 opacity-60 animate-ping" />
      <span className="absolute top-6 right-4 w-1.5 h-1.5 rounded-full bg-red-500 opacity-70" />
      <span className="absolute bottom-4 left-4 w-2 h-2 rounded-full bg-rose-300 opacity-60" />
      <span className="absolute top-1 right-12 text-red-400 text-xs">✦</span>
      <span className="absolute bottom-6 right-6 text-red-500 text-xs font-bold">✦</span>

      <svg className="w-24 sm:w-28 h-24 sm:h-28 drop-shadow-sm" viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="42" fill="#fff1f2" />
        <circle cx="42" cy="74" r="5" fill="#dc2626" />
        <circle cx="42" cy="74" r="2" fill="#ffffff" />
        <circle cx="68" cy="74" r="5" fill="#dc2626" />
        <circle cx="68" cy="74" r="2" fill="#ffffff" />
        <path
          d="M32 38 L76 38 L71 62 L38 62 Z"
          fill="#fee2e2"
          stroke="#ef4444"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <line x1="42" y1="38" x2="44" y2="62" stroke="#fca5a5" strokeWidth="1.5" />
        <line x1="53" y1="38" x2="53" y2="62" stroke="#fca5a5" strokeWidth="1.5" />
        <line x1="64" y1="38" x2="62" y2="62" stroke="#fca5a5" strokeWidth="1.5" />
        <line x1="34" y1="46" x2="74" y2="46" stroke="#fca5a5" strokeWidth="1.5" />
        <line x1="36" y1="54" x2="72" y2="54" stroke="#fca5a5" strokeWidth="1.5" />
        <path
          d="M24 28 L30 28 L38 62 L74 62"
          stroke="#dc2626"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M38 66 L68 66"
          stroke="#b91c1c"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

// Inline SVG: BMKO Groceries Basket Promo Illustration
function GroceryBasketIllustration() {
  return (
    <div className="relative w-full h-24 flex items-center justify-center">
      <svg className="w-28 h-24 drop-shadow-sm" viewBox="0 0 120 100" fill="none">
        <circle cx="42" cy="36" r="10" fill="#22c55e" />
        <circle cx="48" cy="32" r="8" fill="#16a34a" />
        <path d="M56 22 L62 42 L52 42 Z" fill="#f97316" />
        <circle cx="72" cy="38" r="9" fill="#ef4444" />
        <rect x="78" y="24" width="12" height="24" rx="2" fill="#38bdf8" />
        <rect x="81" y="20" width="6" height="5" rx="1" fill="#0284c7" />
        <ellipse cx="64" cy="34" rx="10" ry="7" fill="#eab308" />
        <path
          d="M24 45 L96 45 L88 84 L32 84 Z"
          fill="#dc2626"
          stroke="#b91c1c"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M36 45 C36 28, 84 28, 84 45"
          stroke="#991b1b"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        <line x1="38" y1="52" x2="82" y2="52" stroke="#ffffff" strokeWidth="2" opacity="0.8" />
        <line x1="41" y1="60" x2="79" y2="60" stroke="#ffffff" strokeWidth="2" opacity="0.8" />
        <line x1="44" y1="68" x2="76" y2="68" stroke="#ffffff" strokeWidth="2" opacity="0.8" />
        <text
          x="60"
          y="80"
          textAnchor="middle"
          fill="#ffffff"
          fontSize="8"
          fontWeight="900"
          fontFamily="system-ui, sans-serif"
          letterSpacing="0.5"
        >
          BMKO
        </text>
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Camera Barcode Scanner Modal using html5-qrcode
// ---------------------------------------------------------------------------
function BarcodeScannerModal({ open, onClose, onScanSuccess }) {
  const [cameraError, setCameraError] = useState("");
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [lastScannedCode, setLastScannedCode] = useState("");
  const [scanCount, setScanCount] = useState(0);
  const [isStarting, setIsStarting] = useState(true);

  const scannerRef = useRef(null);
  const isScanningRef = useRef(false);
  const lastScanTimeRef = useRef(0);
  const lastScanCodeRef = useRef("");

  const stopScanner = useCallback(async () => {
    if (scannerRef.current && isScanningRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (err) {
        console.warn("Scanner stop error:", err);
      }
      isScanningRef.current = false;
    }
  }, []);

  const startScanner = useCallback(async (camId) => {
    setIsStarting(true);
    setCameraError("");
    await stopScanner();

    const elementId = "pos-interactive-scanner-viewport";
    const el = document.getElementById(elementId);
    if (!el) {
      setTimeout(() => {
        startScanner(camId);
      }, 150);
      return;
    }

    try {
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode(elementId);
      }
      const qr = scannerRef.current;

      const cameraParam = camId ? { deviceId: { exact: camId } } : { facingMode: "environment" };
      const config = {
        fps: 10,
        qrbox: (viewWidth, viewHeight) => {
          const minEdge = Math.min(viewWidth, viewHeight);
          return {
            width: Math.max(200, Math.floor(minEdge * 0.85)),
            height: Math.max(120, Math.floor(minEdge * 0.5)),
          };
        },
        aspectRatio: 1.333333,
      };

      await qr.start(
        cameraParam,
        config,
        (decodedText) => {
          const now = Date.now();
          if (decodedText === lastScanCodeRef.current && now - lastScanTimeRef.current < 1500) {
            return;
          }
          lastScanTimeRef.current = now;
          lastScanCodeRef.current = decodedText;
          setLastScannedCode(decodedText);
          setScanCount((c) => c + 1);
          onScanSuccess(decodedText);
        },
        () => {} // Frame parse error - ignore
      );

      isScanningRef.current = true;
      setIsStarting(false);
    } catch (err) {
      console.error("Scanner start error:", err);
      setCameraError(err?.message || "Camera access was denied or is not supported. You can enter the barcode manually below.");
      setIsStarting(false);
      isScanningRef.current = false;
    }
  }, [onScanSuccess, stopScanner]);

  useEffect(() => {
    if (!open) return;
    let isMounted = true;
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (!isMounted) return;
        if (devices && devices.length > 0) {
          setCameras(devices);
          const backCam = devices.find((d) =>
            d.label?.toLowerCase().includes("back") ||
            d.label?.toLowerCase().includes("rear") ||
            d.label?.toLowerCase().includes("environment")
          );
          const defaultId = backCam ? backCam.id : devices[0].id;
          setSelectedCameraId(defaultId);
          startScanner(defaultId);
        } else {
          startScanner();
        }
      })
      .catch((err) => {
        console.warn("Could not list cameras:", err);
        startScanner();
      });

    return () => {
      isMounted = false;
      stopScanner();
    };
  }, [open, startScanner, stopScanner]);

  const handleSwitchCamera = () => {
    if (cameras.length <= 1) return;
    const currentIdx = cameras.findIndex((c) => c.id === selectedCameraId);
    const nextIdx = (currentIdx + 1) % cameras.length;
    const nextCamId = cameras[nextIdx].id;
    setSelectedCameraId(nextCamId);
    startScanner(nextCamId);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    onScanSuccess(manualCode.trim());
    setLastScannedCode(manualCode.trim());
    setScanCount((c) => c + 1);
    setManualCode("");
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        stopScanner();
        onClose();
      }
    }}>
      <DialogContent className="max-w-md p-4 sm:p-5 bg-white rounded-2xl shadow-2xl">
        <DialogHeader className="pb-2 border-b border-gray-100 pr-10">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-base font-bold text-gray-900">
              <span className="p-1.5 rounded-lg bg-rose-50 text-rose-600">
                <BarcodeIcon className="w-5 h-5 text-rose-600" />
              </span>
              <span>Camera Barcode Scanner</span>
            </DialogTitle>
            {cameras.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSwitchCamera}
                className="text-xs h-8 px-2.5 gap-1.5 text-gray-700 cursor-pointer"
                title="Switch Camera"
              >
                <SwitchCamera className="w-3.5 h-3.5 text-rose-600" />
                <span>Switch Cam</span>
              </Button>
            )}
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            Align the product barcode or QR code inside the viewfinder box.
          </p>
        </DialogHeader>

        {/* Viewfinder Area */}
        <div className="relative mt-3 bg-slate-950 rounded-xl overflow-hidden aspect-[4/3] flex items-center justify-center border-2 border-dashed border-gray-700">
          <div id="pos-interactive-scanner-viewport" className="w-full h-full object-cover" />

          {/* Animated Laser Scanning Line */}
          {!cameraError && (
            <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 h-0.5 bg-red-500 shadow-[0_0_16px_3px_rgba(239,68,68,1)] animate-pulse pointer-events-none z-10" />
          )}

          {/* Scanner Reticle / Corner Target Guides */}
          <div className="absolute inset-8 pointer-events-none z-10 flex flex-col justify-between">
            <div className="flex justify-between">
              <div className="w-6 h-6 border-t-2 border-l-2 border-rose-500 rounded-tl-md" />
              <div className="w-6 h-6 border-t-2 border-r-2 border-rose-500 rounded-tr-md" />
            </div>
            <div className="flex justify-between">
              <div className="w-6 h-6 border-b-2 border-l-2 border-rose-500 rounded-bl-md" />
              <div className="w-6 h-6 border-b-2 border-r-2 border-rose-500 rounded-br-md" />
            </div>
          </div>

          {/* Camera Loading Spinner */}
          {isStarting && !cameraError && (
            <div className="absolute inset-0 bg-slate-950/80 flex flex-col items-center justify-center gap-2 text-white z-20">
              <RefreshCw className="w-6 h-6 animate-spin text-rose-500" />
              <span className="text-xs font-medium">Starting camera...</span>
            </div>
          )}

          {/* Camera Error / Fallback State */}
          {cameraError && (
            <div className="absolute inset-0 bg-slate-900/95 flex flex-col items-center justify-center p-4 text-center text-white z-20">
              <CameraOff className="w-8 h-8 text-rose-500 mb-2" />
              <p className="text-xs font-semibold text-rose-300">Camera Unavailable</p>
              <p className="text-[11px] text-gray-400 mt-1 max-w-xs">{cameraError}</p>
              <p className="text-[10px] text-gray-500 mt-2">Use the manual barcode box below to add items.</p>
            </div>
          )}
        </div>

        {/* Live Scanned Feedback Badge */}
        {lastScannedCode && (
          <div className="mt-2.5 p-2 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between text-xs animate-in fade-in">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <div className="truncate">
                <span className="text-emerald-800 font-bold">Scanned: </span>
                <span className="font-mono font-semibold text-emerald-900">{lastScannedCode}</span>
              </div>
            </div>
            <span className="bg-emerald-600 text-white font-mono text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">
              #{scanCount}
            </span>
          </div>
        )}

        {/* Manual Barcode Entry Fallback */}
        <form onSubmit={handleManualSubmit} className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Or type barcode / SKU manually..."
              className="w-full pl-3 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
            />
          </div>
          <Button type="submit" size="sm" className="bg-rose-600 hover:bg-rose-700 text-white text-xs px-3.5 h-8 rounded-xl font-bold cursor-pointer">
            Add
          </Button>
        </form>

        <DialogFooter className="mt-2 flex items-center justify-between sm:justify-between w-full">
          <span className="text-[11px] text-gray-400">
            Continuous scanning active
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              stopScanner();
              onClose();
            }}
            className="text-xs h-8 px-3 rounded-xl cursor-pointer"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPOS() {
  const { storeSlug } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Navigation base URL (scopes to current store)
  const base = storeSlug ? `/admin/${storeSlug}` : "/admin";

  // ----- Store Context -----
  const [store, setStore] = useState(null);
  const [orderType, setOrderType] = useState("walk_in"); // walk_in | delivery
  const [salesman, setSalesman] = useState("MS BABU STORE");
  const [staffList, setStaffList] = useState([]);

  // ----- Cart / Items Table -----
  const [items, setItems] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [flatDiscountPct, setFlatDiscountPct] = useState(0);
  const [additionalCharges, setAdditionalCharges] = useState({
    packing: 0,
    delivery: 0,
    service: 0,
  });

  // ----- Search & Autocomplete -----
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // ----- Customer Selection -----
  const [customerQuery, setCustomerQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerStats, setCustomerStats] = useState({
    last_visited: "-",
    last_bill_amount: 0,
    most_purchased_item: "0",
    payment_mode: "-",
    due_payment: 0,
    total_purchase: 0,
    loyalty_points: 0,
  });

  // ----- Last Bill State -----
  const [lastBill, setLastBill] = useState(null);
  const [isLoadingLastBill, setIsLoadingLastBill] = useState(false);

  // ----- Held Bills State -----
  const [heldBills, setHeldBills] = useState([]);
  const [showHeldBillsSheet, setShowHeldBillsSheet] = useState(false);

  // ----- Cash Session (Till Control) -----
  const [activeSession, setActiveSession] = useState(null);
  const [showCashControlModal, setShowCashControlModal] = useState(false);
  const [openingAmount, setOpeningAmount] = useState("");
  const [closingAmount, setClosingAmount] = useState("");
  const [closingNote, setClosingNote] = useState("");
  const [zReportData, setZReportData] = useState(null);

  // ----- Modals -----
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", phone: "", email: "", address: "", gstin: "" });
  const [showAddChargesModal, setShowAddChargesModal] = useState(false);
  const [showSplitPayModal, setShowSplitPayModal] = useState(false);
  const [splitAmounts, setSplitAmounts] = useState({ cash: 0, card: 0, upi: 0, credit: 0 });
  const [showCashTenderModal, setShowCashTenderModal] = useState(false);
  const [cashTendered, setCashTendered] = useState("");
  const [pendingPrintAfterPay, setPendingPrintAfterPay] = useState(false);
  const [invoiceLookupQuery, setInvoiceLookupQuery] = useState("");
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [showRedeemCreditModal, setShowRedeemCreditModal] = useState(false);
  const [redeemedCreditAmount, setRedeemedCreditAmount] = useState(0);

  // ----- Mobile & Responsive Layout States -----
  const [mobileActiveTab, setMobileActiveTab] = useState("cart"); // "cart" | "actions"
  const [showMorePayModal, setShowMorePayModal] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // On desktop screen resize, open sidebar automatically
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth >= 1024) {
      setSidebarOpen(true);
    }
  }, []);

  // ----- Quick Catalog Picker Modal -----
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [catalogFilterTitle, setCatalogFilterTitle] = useState("Popular Products");
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);

  // ----- Barcode Scanner Modal -----
  const [showScannerModal, setShowScannerModal] = useState(false);

  // System status & Offline POS sync state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [syncStats, setSyncStats] = useState({ queued: 0, in_flight: 0, applied: 0, failed: 0, pendingRevenue: 0 });
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncHubModal, setShowSyncHubModal] = useState(false);
  const [cachedCatalogMeta, setCachedCatalogMeta] = useState({ count: 0, lastSync: null });
  const [offlineOrdersList, setOfflineOrdersList] = useState([]);
  const [isPreCaching, setIsPreCaching] = useState(false);

  // ----- Live Clock State -----
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formattedDate = useMemo(() => {
    return currentTime.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [currentTime]);

  const formattedTime = useMemo(() => {
    return currentTime.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).toUpperCase();
  }, [currentTime]);

  // -------------------------------------------------------------
  // Load Store & Initial Data
  // -------------------------------------------------------------
  const fetchStoreData = useCallback(async () => {
    try {
      const res = await api.get("/stores/current");
      if (res?.data) {
        setStore(res.data);
        if (res.data.name) setSalesman(res.data.name.toUpperCase());
      }
    } catch {
      /* fallback */
    }
  }, []);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await api.get("/employees?limit=50");
      if (res?.data?.items) {
        setStaffList(res.data.items);
      }
    } catch {
      /* fallback */
    }
  }, []);

  const fetchLastBill = useCallback(async () => {
    try {
      setIsLoadingLastBill(true);
      const res = await api.get("/orders/last-pos-bill");
      if (res?.data?.order) {
        setLastBill(res.data.order);
      }
    } catch {
      /* silent */
    } finally {
      setIsLoadingLastBill(false);
    }
  }, []);

  const fetchHeldBills = useCallback(async () => {
    try {
      const res = await api.get("/hold-bills");
      if (res?.data?.items) {
        setHeldBills(res.data.items);
      }
    } catch {
      /* silent */
    }
  }, []);

  const fetchActiveCashSession = useCallback(async () => {
    try {
      const res = await api.get("/cash-sessions/active");
      if (res?.data) {
        setActiveSession(res.data);
      } else {
        setActiveSession(null);
      }
    } catch {
      setActiveSession(null);
    }
  }, []);

  // -------------------------------------------------------------
  // Offline POS Synchronization Handlers
  // -------------------------------------------------------------
  const refreshSyncStats = useCallback(async () => {
    try {
      const stats = await getOfflineQueueStats();
      setSyncStats(stats);
      const meta = await getCachedCatalogMeta();
      setCachedCatalogMeta(meta);
      const offOrders = await getOfflineOrders(25);
      setOfflineOrdersList(offOrders);
    } catch (err) {
      console.warn("Failed reading offline stats:", err);
    }
  }, []);

  const handlePreCacheCatalog = useCallback(async () => {
    if (!navigator.onLine) {
      toast.error("Cannot pre-cache catalog while offline. Connect to internet first.");
      return;
    }
    setIsPreCaching(true);
    try {
      const res = await api.get("/products?limit=500");
      const items = res?.data?.items || [];
      if (items.length > 0) {
        const cached = await cacheProductsCatalog(items);
        toast.success(`Pre-cached ${cached} products for offline barcode scanning!`);
        await refreshSyncStats();
      } else {
        toast.info("No catalog products returned to cache");
      }
    } catch (err) {
      toast.error(errMsg(err, "Failed to cache catalog"));
    } finally {
      setIsPreCaching(false);
    }
  }, [refreshSyncStats]);

  const handleSyncNow = useCallback(async (isAuto = false) => {
    if (!navigator.onLine || isSyncing) return;
    setIsSyncing(true);
    try {
      const res = await syncPendingQueue(api);
      if (res.syncedCount > 0) {
        playBeep(1800, 0.15);
        toast.success(`Synced ${res.syncedCount} offline bill(s) to server successfully!`);
      } else if (!isAuto) {
        if (res.error) {
          toast.warning(res.error);
        } else {
          toast.info("All offline bills are already synchronized.");
        }
      }
      await refreshSyncStats();
      fetchLastBill();
    } catch (err) {
      if (!isAuto) {
        toast.error(errMsg(err, "Sync failed. Will retry automatically when online."));
      }
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, refreshSyncStats, fetchLastBill]);

  useEffect(() => {
    fetchStoreData();
    fetchStaff();
    fetchLastBill();
    fetchHeldBills();
    fetchActiveCashSession();
    refreshSyncStats();

    // Auto-warm offline database & pre-cache if empty
    openOfflineDB().then(async () => {
      const meta = await getCachedCatalogMeta();
      if (meta.count === 0 && navigator.onLine) {
        handlePreCacheCatalog();
      }
    }).catch(console.warn);

    const handleOnline = () => {
      setIsOnline(true);
      toast.success("Internet reconnected. Syncing offline bills...");
      handleSyncNow(true);
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning("Internet disconnected. Switching to Offline Billing Mode.");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Periodic sync poll every 30 seconds when online
    const syncInterval = setInterval(() => {
      if (navigator.onLine) {
        handleSyncNow(true);
      }
    }, 30000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(syncInterval);
    };
  }, [fetchStoreData, fetchStaff, fetchLastBill, fetchHeldBills, fetchActiveCashSession, refreshSyncStats, handlePreCacheCatalog, handleSyncNow]);

  // -------------------------------------------------------------
  // Customer Stats Loader
  // -------------------------------------------------------------
  useEffect(() => {
    if (!selectedCustomer?.id) {
      setCustomerStats({
        last_visited: "-",
        last_bill_amount: 0,
        most_purchased_item: "0",
        payment_mode: "-",
        due_payment: 0,
        total_purchase: 0,
        loyalty_points: 0,
      });
      return;
    }
    let cancelled = false;
    api.get(`/customers/${selectedCustomer.id}/pos-summary`)
      .then((res) => {
        if (!cancelled && res?.data) {
          setCustomerStats(res.data);
        }
      })
      .catch(() => {
        /* fallback default */
      });
    return () => { cancelled = true; };
  }, [selectedCustomer]);

  // -------------------------------------------------------------
  // Product Search & Barcode Scan
  // -------------------------------------------------------------
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setSearchResults([]);
      setIsSearching(false);
      setShowSearchDropdown(false);
      return;
    }
    setIsSearching(true);
    setShowSearchDropdown(true);
    const timer = setTimeout(async () => {
      if (!navigator.onLine) {
        try {
          const localItems = await searchOfflineCatalog(trimmed);
          setSearchResults(localItems);
        } catch {
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
        return;
      }

      try {
        const res = await api.get(`/products?q=${encodeURIComponent(trimmed)}&limit=20`);
        const items = res?.data?.items || [];
        setSearchResults(items);
      } catch (err) {
        // Fallback to offline catalog on network error
        try {
          const localItems = await searchOfflineCatalog(trimmed);
          setSearchResults(localItems);
        } catch {
          setSearchResults([]);
        }
      } finally {
        setIsSearching(false);
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Click outside listener to dismiss search dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target)
      ) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Add a product to the billing table
  const addItemToCart = useCallback((product, variant = null) => {
    playBeep(1200, 0.07);
    setItems((prev) => {
      const pId = product.id || product._id;
      const vId = variant ? (variant.id || variant._id) : null;
      const key = `${pId}:${vId || ""}`;
      const existingIdx = prev.findIndex((i) => i.key === key);

      const price = Number(variant?.selling_price || product.selling_price || 0);
      const mrp = Number(variant?.mrp || product.mrp || price);
      const barcode = variant?.barcode || product.barcode || product.sku || "N/A";
      const unit = variant?.unit || product.unit || "PCS";
      const name = variant ? `${product.name} (${variant.name})` : product.name;

      if (existingIdx > -1) {
        const copy = [...prev];
        const item = copy[existingIdx];
        const newQty = Number((item.qty + 1).toFixed(2));
        const effectiveUnitCost = Number(item.unitCost) || price;
        copy[existingIdx] = {
          ...item,
          qty: newQty,
          netAmount: Number((newQty * effectiveUnitCost).toFixed(2)),
        };
        return copy;
      }

      const initialFlatDisc = mrp > price ? Number((mrp - price).toFixed(2)) : 0;
      const initialPctDisc = mrp > price && mrp > 0 ? Number((((mrp - price) / mrp) * 100).toFixed(2)) : 0;
      const initialUnitCost = price;
      const newItem = {
        key,
        productId: pId,
        variantId: vId,
        itemcode: barcode,
        productName: name,
        product,
        variant,
        qty: 1,
        mrp,
        unit,
        discountFlat: initialFlatDisc,
        discountPct: initialPctDisc,
        unitCost: initialUnitCost,
        netAmount: initialUnitCost,
      };
      return [newItem, ...prev];
    });

    setSearchQuery("");
    setShowSearchDropdown(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Handle direct barcode scanner enter key press
  const handleSearchKeyDown = async (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      e.preventDefault();
      const q = searchQuery.trim().toLowerCase();
      let match = searchResults.find(
        (p) => (p.barcode && p.barcode.toLowerCase() === q) ||
          (p.sku && p.sku.toLowerCase() === q)
      );

      // If not in current searchResults, look up in offline IndexedDB catalog immediately
      if (!match) {
        try {
          const localItems = await searchOfflineCatalog(q);
          match = localItems.find(
            (p) => (p.barcode && p.barcode.toLowerCase() === q) ||
              (p.sku && p.sku.toLowerCase() === q)
          ) || localItems[0];
        } catch {
          /* ignore */
        }
      }

      if (match) {
        addItemToCart(match);
      } else if (searchResults.length === 1) {
        addItemToCart(searchResults[0]);
      } else if (searchResults.length > 1) {
        addItemToCart(searchResults[0]);
      } else {
        toast.error(`No product found for "${searchQuery}"`);
      }
    } else if (e.key === "Escape") {
      setShowSearchDropdown(false);
    }
  };

  // Handle Barcode Scanned via Camera or Scanner Modal
  const handleBarcodeScanned = useCallback(async (scannedCode) => {
    if (!scannedCode) return;
    const code = scannedCode.trim();
    playBeep(1400, 0.08);

    let match = null;

    // 1. Check if it matches existing products in catalogProducts or searchResults
    match = (catalogProducts || []).find(
      (p) => (p.barcode && p.barcode.toLowerCase() === code.toLowerCase()) ||
        (p.sku && p.sku.toLowerCase() === code.toLowerCase())
    ) || (searchResults || []).find(
      (p) => (p.barcode && p.barcode.toLowerCase() === code.toLowerCase()) ||
        (p.sku && p.sku.toLowerCase() === code.toLowerCase())
    );

    // 2. Query online backend
    if (!match && navigator.onLine) {
      try {
        const res = await api.get(`/products?q=${encodeURIComponent(code)}&limit=10`);
        const items = res?.data?.items || [];
        match = items.find(
          (p) => (p.barcode && p.barcode.toLowerCase() === code.toLowerCase()) ||
            (p.sku && p.sku.toLowerCase() === code.toLowerCase()) ||
            (p.variants && p.variants.some((v) => v.barcode && v.barcode.toLowerCase() === code.toLowerCase()))
        ) || items[0];
      } catch {
        /* ignore network error, fallback to offline */
      }
    }

    // 3. Fallback to offline catalog (IndexedDB)
    if (!match) {
      try {
        const localItems = await searchOfflineCatalog(code);
        match = localItems.find(
          (p) => (p.barcode && p.barcode.toLowerCase() === code.toLowerCase()) ||
            (p.sku && p.sku.toLowerCase() === code.toLowerCase())
        ) || localItems[0];
      } catch {
        /* ignore */
      }
    }

    if (match) {
      addItemToCart(match);
      toast.success(`Added: ${match.name}`, { icon: "✅" });
    } else {
      setSearchQuery(code);
      toast.error(`No product found with barcode "${code}"`, { icon: "⚠️" });
    }
  }, [catalogProducts, searchResults, addItemToCart]);

  // Open Quick Product Catalog (e.g. from category pills)
  const openCategoryQuickAdd = async (categoryName) => {
    setCatalogFilterTitle(categoryName);
    setShowCatalogModal(true);
    setIsLoadingCatalog(true);
    try {
      const res = await api.get(`/products?limit=24`);
      setCatalogProducts(res?.data?.items || []);
    } catch {
      setCatalogProducts([]);
    } finally {
      setIsLoadingCatalog(false);
    }
  };

  // -------------------------------------------------------------
  // Pricing & Calculations (Two-way Unit Cost & Discount Sync)
  // -------------------------------------------------------------
  const updateItemQty = (index, delta) => {
    setItems((prev) => {
      const copy = [...prev];
      const it = copy[index];
      const newQty = Number(Math.max(0.1, it.qty + delta).toFixed(2));
      const uc = Number(it.unitCost) || 0;
      copy[index] = {
        ...it,
        qty: newQty,
        netAmount: Number((newQty * uc).toFixed(2)),
      };
      return copy;
    });
  };

  const setItemQtyDirect = (index, value) => {
    const val = parseFloat(value);
    if (isNaN(val) || val <= 0) return;
    setItems((prev) => {
      const copy = [...prev];
      const it = copy[index];
      const uc = Number(it.unitCost) || 0;
      copy[index] = {
        ...it,
        qty: val,
        netAmount: Number((val * uc).toFixed(2)),
      };
      return copy;
    });
  };

  const updateItemUnitCost = (index, val) => {
    setItems((prev) => {
      const copy = [...prev];
      const it = copy[index];
      if (val === "") {
        copy[index] = {
          ...it,
          unitCost: "",
          discountFlat: 0,
          discountPct: 0,
          netAmount: 0,
        };
        return copy;
      }
      const newCost = Math.max(0, parseFloat(val) || 0);
      const mrp = Number(it.mrp) || newCost;

      let discountFlat = 0;
      let discountPct = 0;
      if (mrp > newCost) {
        discountFlat = Number((mrp - newCost).toFixed(2));
        discountPct = mrp > 0 ? Number((((mrp - newCost) / mrp) * 100).toFixed(2)) : 0;
      }

      copy[index] = {
        ...it,
        unitCost: newCost,
        discountFlat,
        discountPct,
        netAmount: Number(((it.qty || 1) * newCost).toFixed(2)),
      };
      return copy;
    });
  };

  const updateItemDiscountFlat = (index, val) => {
    setItems((prev) => {
      const copy = [...prev];
      const it = copy[index];
      if (val === "") {
        const mrp = Number(it.mrp) || 0;
        copy[index] = {
          ...it,
          discountFlat: "",
          discountPct: 0,
          unitCost: mrp,
          netAmount: Number(((it.qty || 1) * mrp).toFixed(2)),
        };
        return copy;
      }
      const flat = Math.max(0, parseFloat(val) || 0);
      const mrp = Number(it.mrp) || (Number(it.unitCost) + flat);
      const unitCost = Math.max(0, Number((mrp - flat).toFixed(2)));
      const discountPct = mrp > 0 ? Number(((flat / mrp) * 100).toFixed(2)) : 0;

      copy[index] = {
        ...it,
        discountFlat: flat,
        discountPct,
        unitCost,
        netAmount: Number(((it.qty || 1) * unitCost).toFixed(2)),
      };
      return copy;
    });
  };

  const updateItemDiscountPct = (index, val) => {
    setItems((prev) => {
      const copy = [...prev];
      const it = copy[index];
      if (val === "") {
        const mrp = Number(it.mrp) || 0;
        copy[index] = {
          ...it,
          discountPct: "",
          discountFlat: 0,
          unitCost: mrp,
          netAmount: Number(((it.qty || 1) * mrp).toFixed(2)),
        };
        return copy;
      }
      const pct = Math.max(0, Math.min(100, parseFloat(val) || 0));
      const mrp = Number(it.mrp) || Number(it.unitCost) || 0;
      const flat = Number(((mrp * pct) / 100).toFixed(2));
      const unitCost = Math.max(0, Number((mrp - flat).toFixed(2)));

      copy[index] = {
        ...it,
        discountPct: pct,
        discountFlat: flat,
        unitCost,
        netAmount: Number(((it.qty || 1) * unitCost).toFixed(2)),
      };
      return copy;
    });
  };

  const handleItemInputBlur = (index, field) => {
    setItems((prev) => {
      const copy = [...prev];
      const it = copy[index];
      if (field === "unitCost") {
        const uc = it.unitCost === "" ? Number(it.mrp) || 0 : Number(it.unitCost);
        copy[index] = {
          ...it,
          unitCost: uc,
          netAmount: Number(((it.qty || 1) * uc).toFixed(2)),
        };
      } else if (field === "discountFlat") {
        const df = it.discountFlat === "" ? 0 : Number(it.discountFlat);
        copy[index] = { ...it, discountFlat: df };
      } else if (field === "discountPct") {
        const dp = it.discountPct === "" ? 0 : Number(it.discountPct);
        copy[index] = { ...it, discountPct: dp };
      }
      return copy;
    });
  };

  const removeItem = (index) => {
    playBeep(900, 0.05);
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const clearBill = () => {
    if (items.length === 0) return;
    if (window.confirm("Are you sure you want to clear the current bill?")) {
      setItems([]);
      setRemarks("");
      setSelectedCustomer(null);
      setCustomerQuery("");
      setFlatDiscountPct(0);
      setAppliedCoupon(null);
      setRedeemedCreditAmount(0);
      setAdditionalCharges({ packing: 0, delivery: 0, service: 0 });
      toast.info("Bill cleared");
    }
  };

  // Bill Totals Calculation
  const totals = useMemo(() => {
    let totalQty = 0;
    let totalMRP = 0;
    let itemsNetTotal = 0;
    let totalDiscount = 0;

    items.forEach((it) => {
      totalQty += it.qty;
      const mrpLine = it.mrp * it.qty;
      totalMRP += mrpLine;
      itemsNetTotal += it.netAmount;
      totalDiscount += Math.max(0, mrpLine - it.netAmount);
    });

    let billLevelDiscAmount = 0;
    if (flatDiscountPct > 0) {
      billLevelDiscAmount = Number(((itemsNetTotal * flatDiscountPct) / 100).toFixed(2));
    }

    let couponDiscountAmount = 0;
    if (appliedCoupon?.discount_amount) {
      couponDiscountAmount = Number(appliedCoupon.discount_amount);
    }

    const creditDiscount = Number(redeemedCreditAmount) || 0;

    const addChargesSum =
      (Number(additionalCharges.packing) || 0) +
      (Number(additionalCharges.delivery) || 0) +
      (Number(additionalCharges.service) || 0);

    const subtotal = Math.max(0, itemsNetTotal - billLevelDiscAmount - couponDiscountAmount - creditDiscount);
    const rawTotal = subtotal + addChargesSum;
    const roundedTotal = Math.round(rawTotal);
    const roundoff = Number((roundedTotal - rawTotal).toFixed(2));
    const taxAmount = Number(((roundedTotal * 0.05) / 1.05).toFixed(2));

    return {
      totalQty: Number(totalQty.toFixed(2)),
      totalMRP: Number(totalMRP.toFixed(2)),
      totalDiscount: Number((totalDiscount + billLevelDiscAmount + couponDiscountAmount + creditDiscount).toFixed(2)),
      redeemedCredit: creditDiscount,
      taxAmount,
      addChargesSum,
      roundoff,
      grandTotal: roundedTotal,
    };
  }, [items, flatDiscountPct, appliedCoupon, redeemedCreditAmount, additionalCharges]);

  // -------------------------------------------------------------
  // Customer Search & Creation
  // -------------------------------------------------------------
  useEffect(() => {
    if (!customerQuery.trim() || selectedCustomer) {
      setCustomerResults([]);
      setShowCustomerDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/customers/search?q=${encodeURIComponent(customerQuery.trim())}&limit=6`);
        const items = res?.data?.items || [];
        setCustomerResults(items);
        setShowCustomerDropdown(items.length > 0);
      } catch {
        setCustomerResults([]);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [customerQuery, selectedCustomer]);

  const selectCustomer = (cust) => {
    setSelectedCustomer(cust);
    setCustomerQuery(cust.name || cust.phone || "Customer");
    setShowCustomerDropdown(false);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerQuery("");
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomer.name || !newCustomer.phone) {
      toast.error("Customer name and phone number are required");
      return;
    }
    try {
      const res = await api.post("/customers", {
        name: newCustomer.name,
        phone: newCustomer.phone,
        email: newCustomer.email || `${newCustomer.phone}@walkin.babubaazar.in`,
        address: newCustomer.address ? { line1: newCustomer.address } : null,
        gstin: newCustomer.gstin || null,
      });
      toast.success("Customer created successfully");
      const created = res.data;
      setSelectedCustomer(created);
      setCustomerQuery(created.name);
      setShowCustomerModal(false);
      setNewCustomer({ name: "", phone: "", email: "", address: "", gstin: "" });
    } catch (err) {
      toast.error(errMsg(err, "Failed to create customer"));
    }
  };

  // -------------------------------------------------------------
  // Checkout & Payment Execution (with Offline Mode Fallback)
  // -------------------------------------------------------------
  const resetCart = () => {
    setItems([]);
    setRemarks("");
    setSelectedCustomer(null);
    setCustomerQuery("");
    setFlatDiscountPct(0);
    setAppliedCoupon(null);
    setRedeemedCreditAmount(0);
    setAdditionalCharges({ packing: 0, delivery: 0, service: 0 });
    setShowSplitPayModal(false);
    setShowCashTenderModal(false);
    setShowRedeemCreditModal(false);
    setShowMorePayModal(false);
  };

  const executeCheckout = async (paymentMethod, splitPayload = null, shouldPrint = false) => {
    if (items.length === 0) {
      toast.error("Please add at least one item to checkout");
      return;
    }
    if (paymentMethod === "credit" && !selectedCustomer?.id) {
      toast.error("Credit / Udhar sale requires selecting a customer");
      return;
    }

    setIsSubmitting(true);

    const checkoutPayload = {
      channel: "pos",
      payment_method: paymentMethod === "cash_print" ? "cash_print" : paymentMethod,
      notes: remarks ? `cashier ${salesman} | ${remarks}` : `cashier ${salesman}`,
      extra_discount_amount: totals.totalDiscount,
      wallet_amount: totals.redeemedCredit || 0,
      packing_charge: Number(additionalCharges.packing) || 0,
      delivery_charge: Number(additionalCharges.delivery) || 0,
      service_charge: Number(additionalCharges.service) || 0,
      items: items.map((it) => ({
        product_id: it.productId,
        variant_id: it.variantId || null,
        qty: it.qty,
        unit_price: it.unitCost,
      })),
      customer: selectedCustomer
        ? {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          phone: selectedCustomer.phone,
          email: selectedCustomer.email,
        }
        : { name: "Walk in Customer", phone: "" },
    };

    if (paymentMethod === "credit") {
      checkoutPayload.due_customer_id = selectedCustomer.id;
      checkoutPayload.amount_paid = 0;
    }

    if (paymentMethod === "split" && splitPayload) {
      checkoutPayload.split_breakdown = splitPayload;
    }

    // Helper to process and persist offline bill locally
    const processOfflineOrder = async (alertPrefix = "") => {
      try {
        const { offlineOrder } = await queueOfflineOrder({
          checkoutPayload,
          items,
          totals,
          selectedCustomer,
          store,
          salesman,
        });

        playBeep(1800, 0.15);
        toast.warning(
          `${alertPrefix}Bill saved offline (${offlineOrder.order_number})! Will sync automatically when online.`,
          { duration: 4000 }
        );

        setLastBill(offlineOrder);

        if (shouldPrint || paymentMethod.includes("print")) {
          triggerInvoicePrint(offlineOrder);
        }

        resetCart();
        await refreshSyncStats();
      } catch (saveErr) {
        toast.error("Failed to save offline bill: " + saveErr.message);
      }
    };

    // If client is disconnected, immediately process as offline bill
    if (!navigator.onLine || !isOnline) {
      await processOfflineOrder();
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await api.post("/orders/checkout", checkoutPayload);
      const createdOrder = res.data;

      playBeep(1800, 0.15);
      toast.success(`Bill generated successfully! Order #${createdOrder.order_number}`);

      setLastBill(createdOrder);

      if (shouldPrint || paymentMethod.includes("print")) {
        triggerInvoicePrint(createdOrder);
      }

      resetCart();
      refreshSyncStats();
    } catch (err) {
      // Auto-fallback if connection timed out or dropped during transmission
      const isNetError = !err.response || err.code === "ERR_NETWORK" || err.message?.includes("Network Error");
      if (isNetError) {
        await processOfflineOrder("Network disconnected. ");
      } else {
        toast.error(errMsg(err, "Checkout failed"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // Invoice Printing
  // -------------------------------------------------------------
  const triggerInvoicePrint = (order) => {
    if (!order) return;
    try {
      const cfg = {
        store_name: store?.name || "MS BABU STORE",
        address: store?.address?.line1 || "Indirapuram, Ghaziabad",
        phone: store?.contact?.phone || "",
        email: store?.contact?.email || "",
        gstin: store?.gst?.gstin || "",
      };
      const doc = buildInvoicePDF({ order, invoice: null, cfg, paperSize: "80mm" });
      printPDF(doc);
    } catch (err) {
      toast.error("Could not trigger thermal printer: " + err.message);
    }
  };

  // -------------------------------------------------------------
  // Hold & Recall Bills
  // -------------------------------------------------------------
  const handleHoldBill = async (shouldPrint = false) => {
    if (items.length === 0) {
      toast.error("Cart is empty, cannot hold bill");
      return;
    }
    try {
      await api.post("/hold-bills", {
        hold_name: selectedCustomer?.name || `Bill #${heldBills.length + 1}`,
        items: items.map((it) => ({
          productId: it.productId,
          variantId: it.variantId,
          qty: it.qty,
          unitCost: it.unitCost,
          mrp: it.mrp,
          itemcode: it.itemcode,
          productName: it.productName,
          discountFlat: it.discountFlat,
          discountPct: it.discountPct,
        })),
        customer: selectedCustomer,
        totals: { grandTotal: totals.grandTotal },
        note: remarks,
      });
      playBeep(1000, 0.1);
      toast.success("Bill held successfully");
      fetchHeldBills();

      if (shouldPrint) {
        toast.info("Printing hold token...");
      }

      setItems([]);
      setRemarks("");
      setSelectedCustomer(null);
      setCustomerQuery("");
    } catch (err) {
      toast.error(errMsg(err, "Failed to hold bill"));
    }
  };

  const handleRestoreHeldBill = (held) => {
    if (items.length > 0) {
      if (!window.confirm("Current bill items will be replaced. Continue?")) return;
    }
    const restoredItems = (held.items || []).map((it) => ({
      key: `${it.productId}:${it.variantId || ""}`,
      productId: it.productId,
      variantId: it.variantId,
      itemcode: it.itemcode || "N/A",
      productName: it.productName || "Product",
      qty: it.qty || 1,
      mrp: it.mrp || it.unitCost,
      unit: "PCS",
      discountFlat: it.discountFlat || 0,
      discountPct: it.discountPct || 0,
      unitCost: it.unitCost,
      netAmount: Number(((it.qty || 1) * it.unitCost).toFixed(2)),
    }));

    setItems(restoredItems);
    setSelectedCustomer(held.customer || null);
    if (held.customer?.name) setCustomerQuery(held.customer.name);
    setRemarks(held.note || "");
    setShowHeldBillsSheet(false);
    setMobileActiveTab("cart");

    api.delete(`/hold-bills/${held.id || held._id}`).then(fetchHeldBills).catch(() => { });
    toast.success("Held bill restored to counter");
  };

  // -------------------------------------------------------------
  // Cash Session (Till Control) Actions
  // -------------------------------------------------------------
  const handleOpenCashSession = async () => {
    const amt = parseFloat(openingAmount);
    if (isNaN(amt) || amt < 0) {
      toast.error("Please enter a valid opening cash amount");
      return;
    }
    try {
      const res = await api.post("/cash-sessions", { opening_amount: amt });
      setActiveSession(res.data);
      toast.success("Cash drawer session opened");
      setShowCashControlModal(false);
      setOpeningAmount("");
    } catch (err) {
      toast.error(errMsg(err, "Failed to open cash session"));
    }
  };

  const handleCloseCashSession = async () => {
    if (!activeSession?.id && !activeSession?._id) return;
    const sessId = activeSession.id || activeSession._id;
    const amt = parseFloat(closingAmount);
    if (isNaN(amt) || amt < 0) {
      toast.error("Please enter counted closing cash amount");
      return;
    }
    try {
      await api.patch(`/cash-sessions/${sessId}/close`, {
        closing_amount: amt,
        note: closingNote || undefined,
      });
      toast.success("Cash session closed successfully");
      fetchActiveCashSession();
      setShowCashControlModal(false);
      setClosingAmount("");
      setClosingNote("");
    } catch (err) {
      toast.error(errMsg(err, "Failed to close cash session"));
    }
  };

  const handleViewZReport = async () => {
    if (!activeSession?.id && !activeSession?._id) return;
    const sessId = activeSession.id || activeSession._id;
    try {
      const res = await api.get(`/cash-sessions/${sessId}/z-report`);
      setZReportData(res.data);
    } catch (err) {
      toast.error(errMsg(err, "Failed to load Z-Report"));
    }
  };

  // -------------------------------------------------------------
  // Global Keyboard Shortcuts (F1 - F12)
  // -------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F2 or Alt+S: Focus Barcode Search
      if (e.key === "F2" || (e.altKey && e.key.toLowerCase() === "s")) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      // Alt+C: Focus Customer
      if (e.altKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        setShowCustomerModal(true);
        return;
      }
      // F3: Add Product or Catalog
      if (e.key === "F3") {
        e.preventDefault();
        openCategoryQuickAdd("All Products");
        return;
      }
      // F4: Cash Payment (Tender Modal)
      if (e.key === "F4") {
        e.preventDefault();
        if (items.length === 0) {
          toast.warning("Please add at least one item to the cart first", { icon: "🛒" });
          searchInputRef.current?.focus();
          return;
        }
        setPendingPrintAfterPay(false);
        setCashTendered(totals.grandTotal.toString());
        setShowCashTenderModal(true);
        return;
      }
      // F5: UPI Payment
      if (e.key === "F5") {
        e.preventDefault();
        if (items.length === 0) {
          toast.warning("Please add at least one item to the cart first", { icon: "🛒" });
          searchInputRef.current?.focus();
          return;
        }
        executeCheckout("upi");
        return;
      }
      // F6: Saved / Hold Bills
      if (e.key === "F6") {
        e.preventDefault();
        setShowHeldBillsSheet(true);
        return;
      }
      // F7: Hold Bill / Hold & Print
      if (e.key === "F7") {
        e.preventDefault();
        if (items.length === 0) {
          toast.warning("Please add at least one item to the cart first", { icon: "🛒" });
          searchInputRef.current?.focus();
          return;
        }
        handleHoldBill(false);
        return;
      }
      // F8: Cash & Print
      if (e.key === "F8") {
        e.preventDefault();
        if (items.length === 0) {
          toast.warning("Please add at least one item to the cart first", { icon: "🛒" });
          searchInputRef.current?.focus();
          return;
        }
        setPendingPrintAfterPay(true);
        setCashTendered(totals.grandTotal.toString());
        setShowCashTenderModal(true);
        return;
      }
      // F9: Card & Print
      if (e.key === "F9") {
        e.preventDefault();
        if (items.length === 0) {
          toast.warning("Please add at least one item to the cart first", { icon: "🛒" });
          searchInputRef.current?.focus();
          return;
        }
        executeCheckout("card", null, true);
        return;
      }
      // F10: UPI & Print
      if (e.key === "F10") {
        e.preventDefault();
        if (items.length === 0) {
          toast.warning("Please add at least one item to the cart first", { icon: "🛒" });
          searchInputRef.current?.focus();
          return;
        }
        executeCheckout("upi", null, true);
        return;
      }
      // F11: Pay Later (Credit)
      if (e.key === "F11") {
        e.preventDefault();
        if (items.length === 0) {
          toast.warning("Please add at least one item to the cart first", { icon: "🛒" });
          searchInputRef.current?.focus();
          return;
        }
        if (!selectedCustomer?.id) {
          toast.error("Credit / Udhar sale requires selecting a customer");
          setShowCustomerModal(true);
          return;
        }
        executeCheckout("credit");
        return;
      }
      // F12: Multiple Pay (Split)
      if (e.key === "F12") {
        e.preventDefault();
        if (items.length === 0) {
          toast.warning("Please add at least one item to the cart first", { icon: "🛒" });
          searchInputRef.current?.focus();
          return;
        }
        setSplitAmounts({ cash: totals.grandTotal, card: 0, upi: 0, credit: 0 });
        setShowSplitPayModal(true);
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [totals, items, selectedCustomer, executeCheckout, handleHoldBill]);

  return (
    <div className="flex flex-col h-screen w-screen bg-[#f8fafc] text-slate-900 select-none overflow-hidden font-sans">
      
      {/* ═════════════════════════════════════════════════════════════ */}
      {/* ─── 1. TOP HEADER BAR (Mobile-Optimized Responsive Header) ── */}
      {/* ═════════════════════════════════════════════════════════════ */}
      <header className="h-14 bg-white border-b border-gray-200/90 px-2 sm:px-4 flex items-center justify-between shadow-2xs shrink-0 z-30 gap-2">
        
        {/* Left Cluster: Menu Toggle, BMKO Logo, Store Selector */}
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          {/* Hamburger Menu Toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-lg text-gray-700 hover:bg-gray-100 transition active:scale-95"
            title="Toggle Sidebar Navigation"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* BMKO Brand Logo */}
          <Link to={`${base}/dashboard`} className="flex flex-col select-none group shrink-0">
            <div className="flex items-baseline">
              <span className="text-xl sm:text-2xl font-black text-[#e11d48] tracking-tighter leading-none font-sans">
                BMKO
              </span>
              <span className="text-[10px] font-bold text-gray-500 ml-0.5 tracking-tighter">™</span>
            </div>
            <span className="hidden xs:inline text-[9px] sm:text-[9.5px] font-medium text-gray-500 leading-none tracking-tight">
              Trusted by India's Families
            </span>
          </Link>

          {/* Store Selector Pill (Hidden on small mobile screens to keep space) */}
          <div
            onClick={() => setShowCashControlModal(true)}
            className="hidden md:flex items-center gap-2 px-2.5 py-1 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50/60 transition cursor-pointer shadow-2xs"
            title="Active Store Branch / Cashier"
          >
            <div className="w-6 h-6 rounded-lg bg-rose-50 text-[#e11d48] grid place-items-center">
              <Store className="w-3.5 h-3.5" />
            </div>
            <div className="leading-tight">
              <div className="text-xs font-bold text-gray-900 truncate max-w-[110px] lg:max-w-[140px]">
                {store?.name || salesman || "MS BABU STORE"}
              </div>
              <div className="text-[10px] text-gray-500 truncate max-w-[110px] lg:max-w-[140px]">
                {store?.address?.line1 || "Indirapuram, Ghaziabad"}
              </div>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-gray-400 ml-1" />
          </div>
        </div>

        {/* Center: Barcode & Product Search Bar (Elastic & Responsive) */}
        <div className="flex-1 max-w-xl relative min-w-0">
          <div className="relative flex items-center">
            <Search className="w-3.5 sm:w-4 h-3.5 sm:h-4 absolute left-2.5 sm:left-3 text-gray-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => { if (searchQuery.trim()) setShowSearchDropdown(true); }}
              placeholder="Scan barcode or product..."
              className="w-full pl-8 sm:pl-9 pr-14 sm:pr-20 py-1.5 sm:py-2 bg-[#f8fafc] border border-gray-200 rounded-xl text-xs font-medium text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition shadow-2xs"
            />
            {/* Shortcut tag + Red Barcode Icon (Launches Camera Scanner) */}
            <div className="absolute right-2 flex items-center gap-1 sm:gap-1.5">
              <span className="hidden sm:inline font-mono text-[10px] font-bold text-gray-500 bg-gray-200/80 px-1.5 py-0.5 rounded">
                F2
              </span>
              <button
                type="button"
                onClick={() => setShowScannerModal(true)}
                className="text-[#e11d48] hover:bg-rose-50 transition p-1 rounded-lg cursor-pointer flex items-center gap-1 group"
                title="Scan Barcode with Camera (Click to open camera scanner)"
                data-testid="pos-scanner-btn"
              >
                <BarcodeIcon className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>

          {/* Autocomplete Dropdown */}
          {showSearchDropdown && searchQuery.trim() && (
            <div
              ref={dropdownRef}
              className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-80 overflow-y-auto divide-y divide-gray-100"
            >
              {isSearching ? (
                <div className="p-4 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-rose-600" />
                  <span>Searching "{searchQuery}"...</span>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="p-4 text-center text-xs text-gray-500">
                  <p className="font-semibold text-gray-700">No products found for "{searchQuery}"</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">Scan a valid barcode or enter product name.</p>
                </div>
              ) : (
                searchResults.map((p) => {
                  const price = p.selling_price;
                  const mrp = p.mrp || price;
                  const stock = typeof p.stock === "number" ? p.stock : null;
                  return (
                    <div
                      key={p.id || p._id}
                      onClick={() => addItemToCart(p)}
                      className="p-2.5 hover:bg-rose-50/60 cursor-pointer flex items-center justify-between transition group"
                    >
                      <div className="min-w-0 flex-1 pr-3">
                        <div className="text-xs font-semibold text-gray-800 group-hover:text-rose-900 truncate">
                          {p.name}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
                          {p.barcode && <span className="font-mono bg-gray-100 px-1 rounded">{p.barcode}</span>}
                          {p.sku && <span className="font-mono text-gray-400">SKU: {p.sku}</span>}
                          {stock !== null && (
                            <span className={`font-semibold ${stock <= 2 ? "text-rose-600" : "text-emerald-600"}`}>
                              {stock === 0 ? "Out of Stock" : `${stock} in stock`}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-bold text-gray-900">
                          {mrp && mrp !== price && (
                            <span className="text-gray-400 line-through mr-1.5 font-normal">₹{mrp}</span>
                          )}
                          <span className="text-rose-600 font-bold">₹{price}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Right Cluster: Online Status, Utility Icons & Live Clock */}
        <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
          
          {/* Online / Offline Sync Hub Status Pill (Clickable) */}
          <button
            type="button"
            onClick={() => {
              refreshSyncStats();
              setShowSyncHubModal(true);
            }}
            className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-xl border transition shadow-2xs active:scale-95 ${
              isSyncing
                ? "bg-amber-50 text-amber-800 border-amber-200"
                : !isOnline
                ? "bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100"
                : syncStats.queued > 0
                ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
                : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
            }`}
            title="Click to open Offline Sync Hub & Settings"
          >
            {isSyncing ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 text-amber-600 animate-spin" />
                <span className="hidden sm:inline">Syncing...</span>
              </>
            ) : !isOnline ? (
              <>
                <WifiOff className="w-3.5 h-3.5 text-rose-600" />
                <span className="hidden sm:inline">Offline</span>
                {syncStats.queued > 0 && (
                  <span className="bg-rose-600 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold">
                    {syncStats.queued}
                  </span>
                )}
              </>
            ) : syncStats.queued > 0 ? (
              <>
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                <span className="hidden sm:inline">{syncStats.queued} Pending</span>
                <Wifi className="w-3.5 h-3.5 text-amber-600" />
              </>
            ) : (
              <>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="hidden sm:inline">Online</span>
                <Wifi className="w-3.5 h-3.5 text-emerald-600" />
              </>
            )}
          </button>

          {/* Quick Icons (Shown on sm+, hidden on tiny mobile) */}
          <button
            onClick={() => {
              if (lastBill) triggerInvoicePrint(lastBill);
              else toast.info("No recent bill to print");
            }}
            className="hidden sm:flex p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition"
            title="Reprint Last Invoice"
          >
            <Printer className="w-4 h-4" />
          </button>

          <button
            onClick={() => setShowCashControlModal(true)}
            className="hidden sm:flex p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition"
            title="Till Drawer & Cash Control"
          >
            <Settings className="w-4 h-4" />
          </button>

          <button
            onClick={() => toast.info("Notifications: All systems operational. 3 store reminders.")}
            className="relative p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition"
            title="Notifications"
          >
            <Bell className="w-4 h-4" />
            <span className="absolute 0 top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-[#e11d48] text-white text-[8px] font-extrabold flex items-center justify-center">
              3
            </span>
          </button>

          {/* Cashier Avatar Dropdown */}
          <div
            onClick={() => setShowCashControlModal(true)}
            className="flex items-center gap-0.5 sm:gap-1 cursor-pointer pl-0.5"
            title={`Cashier: ${user?.name || "Admin"}`}
          >
            <div className="w-7 h-7 rounded-full bg-rose-100 text-[#e11d48] font-bold text-xs flex items-center justify-center border border-rose-200 shadow-2xs">
              {(user?.name || "AS").slice(0, 2).toUpperCase()}
            </div>
            <ChevronDown className="hidden sm:inline w-3 h-3 text-gray-400" />
          </div>

          {/* Live Date & Time (Desktop only) */}
          <div className="hidden lg:flex flex-col text-right leading-tight pl-2 border-l border-gray-200">
            <span className="text-[10px] font-medium text-gray-500">{formattedDate}</span>
            <span className="text-xs font-extrabold text-gray-900 font-mono tracking-tight">{formattedTime}</span>
          </div>
        </div>
      </header>

      {/* ═════════════════════════════════════════════════════════════ */}
      {/* ─── 2. MAIN WORKSPACE (Responsive Layout with Mobile Tabs) ── */}
      {/* ═════════════════════════════════════════════════════════════ */}
      <div className="flex-1 min-h-0 flex overflow-hidden relative">
        
        {/* Mobile Backdrop for Slide-over Sidebar Drawer */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-xs transition-opacity"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* ─── 2A. LEFT NAVIGATION SIDEBAR (Slide-over on Mobile) ─── */}
        <aside
          className={`
            fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
            w-64 lg:w-56 bg-white border-r border-gray-200/90
            flex flex-col justify-between py-3 px-2.5 shrink-0 overflow-y-auto
            transition-transform duration-200 ease-in-out shadow-2xl lg:shadow-none
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:hidden"}
          `}
        >
          {/* Mobile Drawer Header */}
          <div className="lg:hidden flex items-center justify-between pb-3 mb-2 border-b border-gray-100 px-2">
            <div className="flex items-center gap-2">
              <span className="text-xl font-black text-[#e11d48]">BMKO</span>
              <span className="text-[11px] font-bold text-gray-500">Navigation</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="space-y-1">
            {/* POS (Active Item - Solid Red) */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="w-full bg-[#e11d48] hover:bg-[#be123c] text-white font-bold text-xs px-3.5 py-2.5 rounded-xl shadow-xs flex items-center justify-between transition"
            >
              <div className="flex items-center gap-2.5">
                <ShoppingCart className="w-4 h-4" />
                <span>POS</span>
              </div>
              <span className="bg-white/20 text-white text-[10px] font-mono font-bold px-1.5 py-0.5 rounded">
                F1
              </span>
            </button>

            {/* Products */}
            <Link
              to={`${base}/products`}
              onClick={() => setSidebarOpen(false)}
              className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 font-medium text-xs px-3.5 py-2.5 rounded-xl flex items-center gap-2.5 transition"
            >
              <Package className="w-4 h-4 text-gray-500" />
              <span>Products</span>
            </Link>

            {/* Customers */}
            <Link
              to={`${base}/customers`}
              onClick={() => setSidebarOpen(false)}
              className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 font-medium text-xs px-3.5 py-2.5 rounded-xl flex items-center gap-2.5 transition"
            >
              <Users className="w-4 h-4 text-gray-500" />
              <span>Customers</span>
            </Link>

            {/* Orders */}
            <Link
              to={`${base}/orders`}
              onClick={() => setSidebarOpen(false)}
              className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 font-medium text-xs px-3.5 py-2.5 rounded-xl flex items-center gap-2.5 transition"
            >
              <ClipboardListIcon className="w-4 h-4 text-gray-500" />
              <span>Orders</span>
            </Link>

            {/* Credit Sales */}
            <Link
              to={`${base}/orders?status=credit`}
              onClick={() => setSidebarOpen(false)}
              className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 font-medium text-xs px-3.5 py-2.5 rounded-xl flex items-center gap-2.5 transition"
            >
              <Wallet className="w-4 h-4 text-gray-500" />
              <span>Credit Sales</span>
            </Link>

            {/* Reports */}
            <Link
              to={`${base}/reports`}
              onClick={() => setSidebarOpen(false)}
              className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 font-medium text-xs px-3.5 py-2.5 rounded-xl flex items-center gap-2.5 transition"
            >
              <BarChart3 className="w-4 h-4 text-gray-500" />
              <span>Reports</span>
            </Link>

            {/* Inventory */}
            <Link
              to={`${base}/inventory`}
              onClick={() => setSidebarOpen(false)}
              className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 font-medium text-xs px-3.5 py-2.5 rounded-xl flex items-center gap-2.5 transition"
            >
              <Boxes className="w-4 h-4 text-gray-500" />
              <span>Inventory</span>
            </Link>

            {/* Deal Bazaar with HOT badge */}
            <Link
              to={`${base}/deal-bazaar`}
              onClick={() => setSidebarOpen(false)}
              className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 font-medium text-xs px-3.5 py-2.5 rounded-xl flex items-center justify-between transition"
            >
              <div className="flex items-center gap-2.5">
                <Flame className="w-4 h-4 text-[#e11d48]" />
                <span>Deal Bazaar</span>
              </div>
              <span className="bg-[#e11d48] text-white text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded-full tracking-wider">
                HOT
              </span>
            </Link>

            {/* Suppliers */}
            <Link
              to={`${base}/suppliers`}
              onClick={() => setSidebarOpen(false)}
              className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 font-medium text-xs px-3.5 py-2.5 rounded-xl flex items-center gap-2.5 transition"
            >
              <Truck className="w-4 h-4 text-gray-500" />
              <span>Suppliers</span>
            </Link>

            {/* Settings */}
            <Link
              to={`${base}/store-settings`}
              onClick={() => setSidebarOpen(false)}
              className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 font-medium text-xs px-3.5 py-2.5 rounded-xl flex items-center gap-2.5 transition"
            >
              <Settings className="w-4 h-4 text-gray-500" />
              <span>Settings</span>
            </Link>
          </nav>

          {/* Bottom Promo Card */}
          <div className="mt-auto pt-4">
            <div className="bg-gradient-to-b from-rose-50/50 to-rose-100/60 border border-rose-200/80 rounded-2xl p-3 text-center shadow-2xs">
              <span className="text-xs font-black text-[#e11d48] tracking-tight block">
                BMKO
              </span>
              <p className="text-[11px] font-semibold text-gray-700 leading-tight mt-0.5">
                Groceries for a Better Tomorrow
              </p>
              <GroceryBasketIllustration />
            </div>
          </div>
        </aside>

        {/* ─── 2B. CENTER POS BILLING WORKSPACE ────────────────────── */}
        <main
          className={`
            flex-1 min-w-0 flex flex-col bg-[#f8fafc] p-2 sm:p-3 overflow-hidden
            ${mobileActiveTab === "cart" ? "flex" : "hidden lg:flex"}
          `}
        >
          {/* Mobile Segmented Tab Switcher (< lg screens) */}
          <div className="lg:hidden flex items-center bg-gray-200/80 p-1 rounded-xl mb-2 text-xs font-semibold shrink-0">
            <button
              onClick={() => setMobileActiveTab("cart")}
              className={`flex-1 py-1.5 rounded-lg transition flex items-center justify-center gap-1.5 ${
                mobileActiveTab === "cart" ? "bg-white text-gray-900 shadow-2xs font-bold" : "text-gray-500"
              }`}
            >
              <ShoppingCart className="w-3.5 h-3.5 text-[#e11d48]" />
              <span>Billing Cart ({items.length})</span>
              {items.length > 0 && (
                <span className="font-mono text-[10px] text-rose-600 font-bold ml-1">₹{totals.grandTotal}</span>
              )}
            </button>
            <button
              onClick={() => setMobileActiveTab("actions")}
              className={`flex-1 py-1.5 rounded-lg transition flex items-center justify-center gap-1.5 ${
                mobileActiveTab === "actions" ? "bg-white text-gray-900 shadow-2xs font-bold" : "text-gray-500"
              }`}
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              <span>Actions & Customer</span>
              {selectedCustomer && (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              )}
            </button>
          </div>

          {/* Top Action Row Above Table (Horizontally Scrollable on Mobile) */}
          <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-2.5 shrink-0 overflow-x-auto no-scrollbar pb-1">
            {/* Walk In Customer Dropdown Button (Red) */}
            <button
              onClick={() => setShowCustomerModal(true)}
              className="bg-[#e11d48] hover:bg-[#be123c] text-white font-bold text-xs px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl shadow-xs flex items-center gap-1.5 sm:gap-2 transition cursor-pointer shrink-0"
            >
              <User className="w-3.5 h-3.5" />
              <span className="truncate max-w-[120px] sm:max-w-none">{selectedCustomer ? selectedCustomer.name : "Walk In Customer"}</span>
              <ChevronDown className="w-3.5 h-3.5 opacity-80" />
            </button>

            {/* + Add Product (F3) */}
            <button
              onClick={() => openCategoryQuickAdd("All Products")}
              className="bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-800 font-semibold text-xs px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-xl shadow-2xs flex items-center gap-1.5 transition cursor-pointer shrink-0"
            >
              <Plus className="w-3.5 h-3.5 text-[#e11d48]" />
              <span className="text-[#e11d48] font-bold">Add Product</span>
              <span className="hidden sm:inline bg-gray-100 text-gray-500 font-mono text-[10px] px-1.5 py-0.5 rounded ml-0.5">
                F3
              </span>
            </button>

            {/* Saved Bills (F6) */}
            <button
              onClick={() => setShowHeldBillsSheet(true)}
              className="bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-800 font-semibold text-xs px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-xl shadow-2xs flex items-center gap-1.5 transition cursor-pointer shrink-0"
            >
              <Bookmark className="w-3.5 h-3.5 text-gray-500" />
              <span>Saved Bills</span>
              {heldBills.length > 0 && (
                <span className="bg-amber-500 text-white font-bold text-[10px] px-1.5 rounded-full">
                  {heldBills.length}
                </span>
              )}
              <span className="hidden sm:inline bg-gray-100 text-gray-500 font-mono text-[10px] px-1.5 py-0.5 rounded ml-0.5">
                F6
              </span>
            </button>

            {/* Hold Bill (F7) */}
            <button
              onClick={() => handleHoldBill(false)}
              disabled={items.length === 0}
              className={`bg-white border border-gray-200 font-semibold text-xs px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-xl shadow-2xs flex items-center gap-1.5 transition cursor-pointer shrink-0 ${
                items.length > 0 ? "hover:border-gray-300 hover:bg-gray-50 text-gray-800" : "text-gray-300 cursor-not-allowed"
              }`}
            >
              <PauseCircle className="w-3.5 h-3.5 text-gray-500" />
              <span>Hold Bill</span>
              <span className="hidden sm:inline bg-gray-100 text-gray-500 font-mono text-[10px] px-1.5 py-0.5 rounded ml-0.5">
                F7
              </span>
            </button>
          </div>

          {/* Cart Table Container (Card on Mobile, Spreadsheet on Desktop) */}
          <div className="flex-1 min-h-0 bg-white border border-gray-200/90 rounded-2xl shadow-xs overflow-hidden flex flex-col">
            
            {/* Desktop / Tablet Spreadsheet View (Hidden on mobile < sm) */}
            <div className="hidden sm:block overflow-x-auto flex-1 overflow-y-auto">
              <table className="w-full text-left border-collapse table-fixed">
                <thead className="sticky top-0 bg-[#f8fafc] border-b border-gray-200 text-[11px] font-bold text-gray-500 uppercase tracking-wider z-10 select-none">
                  <tr>
                    <th className="py-2.5 px-2 w-9 text-center">#</th>
                    <th className="py-2.5 px-2.5 w-24">Item Code</th>
                    <th className="py-2.5 px-3 min-w-0">Product</th>
                    <th className="py-2.5 px-2 w-24 text-center">Qty</th>
                    <th className="py-2.5 px-2 w-16 text-right">MRP</th>
                    <th className="py-2.5 px-2 w-12 text-center">Unit</th>
                    <th className="py-2.5 px-2 w-20 text-center">Discount</th>
                    <th className="py-2.5 px-2 w-18 text-center">Add Disc</th>
                    <th className="py-2.5 px-2 w-20 text-right">Unit Cost</th>
                    <th className="py-2.5 px-2 w-24 text-right whitespace-nowrap">Net Amount</th>
                    <th className="py-2.5 px-2 w-10 text-center whitespace-nowrap">
                      <span className="sr-only">Action</span>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100 text-xs">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="py-12 md:py-16 text-center">
                        <div className="flex flex-col items-center justify-center max-w-xl mx-auto px-4">
                          <EmptyCartIllustration />
                          <h3 className="text-xl font-bold text-gray-900 mt-2">
                            Your Cart is Empty
                          </h3>
                          <p className="text-xs text-gray-500 mt-1 max-w-sm text-center">
                            Scan a barcode, search a product or browse categories to start billing
                          </p>
                          <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
                            <button onClick={() => openCategoryQuickAdd("Best Selling")} className="px-3.5 py-1.5 rounded-xl bg-[#fff4ed] text-[#ea580c] border border-[#fed7aa] font-semibold text-xs flex items-center gap-1.5 hover:bg-[#ffedd5]">
                              <Flame className="w-3.5 h-3.5 text-[#ea580c]" /> Best Selling
                            </button>
                            <button onClick={() => openCategoryQuickAdd("Low Selling")} className="px-3.5 py-1.5 rounded-xl bg-[#eff6ff] text-[#2563eb] border border-[#bfdbfe] font-semibold text-xs flex items-center gap-1.5 hover:bg-[#dbeafe]">
                              <TrendingDown className="w-3.5 h-3.5 text-[#2563eb]" /> Low Selling
                            </button>
                            <button onClick={() => openCategoryQuickAdd("Top Selling")} className="px-3.5 py-1.5 rounded-xl bg-[#f0fdf4] text-[#16a34a] border border-[#bbf7d0] font-semibold text-xs flex items-center gap-1.5 hover:bg-[#dcfce7]">
                              <TrendingUp className="w-3.5 h-3.5 text-[#16a34a]" /> Top Selling
                            </button>
                            <button onClick={() => openCategoryQuickAdd("Latest Products")} className="px-3.5 py-1.5 rounded-xl bg-[#faf5ff] text-[#9333ea] border border-[#e9d5ff] font-semibold text-xs flex items-center gap-1.5 hover:bg-[#f3e8ff]">
                              <span className="w-3.5 h-3.5 rounded-full bg-[#9333ea] text-white text-[8px] font-black grid place-items-center">NEW</span> Latest Products
                            </button>
                            <button onClick={() => openCategoryQuickAdd("New Offers")} className="px-3.5 py-1.5 rounded-xl bg-[#fffbeb] text-[#d97706] border border-[#fde68a] font-semibold text-xs flex items-center gap-1.5 hover:bg-[#fef3c7]">
                              <Tag className="w-3.5 h-3.5 text-[#d97706]" /> New Offers
                            </button>
                            <button onClick={() => openCategoryQuickAdd("Buy 1 Get 1 Offers")} className="px-3.5 py-1.5 rounded-xl bg-[#fdf2f8] text-[#db2777] border border-[#fbcfe8] font-semibold text-xs flex items-center gap-1.5 hover:bg-[#fce7f3]">
                              <span className="bg-[#db2777] text-white text-[9px] font-black px-1 rounded">B1G1</span> Buy 1 Get 1
                            </button>
                            <button onClick={() => openCategoryQuickAdd("Near Expiry Clearance")} className="px-3.5 py-1.5 rounded-xl bg-[#fff7ed] text-[#c2410c] border border-[#fed7aa] font-semibold text-xs flex items-center gap-1.5 hover:bg-[#ffedd5]">
                              <Calendar className="w-3.5 h-3.5 text-[#c2410c]" /> Near Expiry
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.map((it, idx) => (
                      <tr key={it.key} className="hover:bg-rose-50/30 transition-colors">
                        <td className="py-2 px-2 text-center text-gray-400 font-mono text-[11px]">{idx + 1}</td>
                        <td className="py-2 px-2.5 font-mono text-[11px] text-gray-600 truncate" title={it.itemcode}>{it.itemcode}</td>
                        <td className="py-2 px-3 min-w-0 font-semibold text-gray-800">
                          <div className="line-clamp-2 break-words leading-tight text-xs font-semibold text-gray-800" title={it.productName}>
                            {it.productName}
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200 overflow-hidden w-22 mx-auto">
                            <button type="button" onClick={() => updateItemQty(idx, -1)} className="w-6 h-7 grid place-items-center text-gray-600 hover:bg-gray-200 cursor-pointer">
                              <Minus className="w-3 h-3" />
                            </button>
                            <input
                              type="number"
                              step="any"
                              value={it.qty}
                              onChange={(e) => setItemQtyDirect(idx, e.target.value)}
                              className="w-10 h-7 text-center font-bold text-gray-800 bg-white border-x border-gray-200 text-xs focus:outline-none"
                            />
                            <button type="button" onClick={() => updateItemQty(idx, 1)} className="w-6 h-7 grid place-items-center text-gray-600 hover:bg-gray-200 cursor-pointer">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-gray-600 text-[11px] whitespace-nowrap">{it.mrp.toFixed(2)}</td>
                        <td className="py-2 px-2 text-center uppercase text-gray-500 text-[11px] font-medium">{it.unit}</td>
                        <td className="py-2 px-2">
                          <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-1.5 py-0.5 w-full max-w-[76px] mx-auto focus-within:bg-white focus-within:border-rose-500 focus-within:ring-1 focus-within:ring-rose-500/20 transition-all">
                            <span className="text-gray-400 text-[10px] font-bold select-none shrink-0 mr-0.5">₹</span>
                            <input
                              type="number"
                              step="any"
                              min="0"
                              value={it.discountFlat}
                              onChange={(e) => updateItemDiscountFlat(idx, e.target.value)}
                              onBlur={() => handleItemInputBlur(idx, "discountFlat")}
                              className="w-full text-right text-xs bg-transparent focus:outline-none font-mono min-w-0"
                            />
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-1.5 py-0.5 w-full max-w-[68px] mx-auto focus-within:bg-white focus-within:border-rose-500 focus-within:ring-1 focus-within:ring-rose-500/20 transition-all">
                            <span className="text-gray-400 text-[10px] font-bold select-none shrink-0 mr-0.5">%</span>
                            <input
                              type="number"
                              step="any"
                              min="0"
                              max="100"
                              value={it.discountPct}
                              onChange={(e) => updateItemDiscountPct(idx, e.target.value)}
                              onBlur={() => handleItemInputBlur(idx, "discountPct")}
                              className="w-full text-right text-xs bg-transparent focus:outline-none font-mono min-w-0"
                            />
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-1.5 py-0.5 w-full max-w-[76px] ml-auto focus-within:bg-white focus-within:border-rose-500 focus-within:ring-1 focus-within:ring-rose-500/20 transition-all">
                            <span className="text-gray-400 text-[10px] font-bold select-none shrink-0 mr-0.5">₹</span>
                            <input
                              type="number"
                              step="any"
                              min="0"
                              value={it.unitCost}
                              onChange={(e) => updateItemUnitCost(idx, e.target.value)}
                              onBlur={() => handleItemInputBlur(idx, "unitCost")}
                              className="w-full text-right text-xs font-bold text-gray-800 bg-transparent focus:outline-none font-mono min-w-0"
                            />
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right font-mono font-bold text-gray-900 whitespace-nowrap">
                          ₹{(Number(it.netAmount) || 0).toFixed(2)}
                        </td>
                        <td className="py-2 px-2 text-center whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            title="Delete item"
                            className="p-1 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors inline-flex items-center justify-center cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Touch-Friendly Card View (< sm screens) */}
            <div className="sm:hidden flex-1 overflow-y-auto p-2 space-y-2">
              {items.length === 0 ? (
                <div className="py-8 text-center px-2">
                  <EmptyCartIllustration />
                  <h3 className="text-base font-bold text-gray-900 mt-2">Your Cart is Empty</h3>
                  <p className="text-[11px] text-gray-500 mt-0.5">Scan barcode or pick a category below</p>
                  
                  {/* Horizontally scrollable category pills on mobile */}
                  <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-3 mt-2">
                    <button onClick={() => openCategoryQuickAdd("Best Selling")} className="px-2.5 py-1 rounded-xl bg-[#fff4ed] text-[#ea580c] border border-[#fed7aa] font-semibold text-[11px] flex items-center gap-1 shrink-0">
                      <Flame className="w-3 h-3 text-[#ea580c]" /> Best Selling
                    </button>
                    <button onClick={() => openCategoryQuickAdd("Top Selling")} className="px-2.5 py-1 rounded-xl bg-[#f0fdf4] text-[#16a34a] border border-[#bbf7d0] font-semibold text-[11px] flex items-center gap-1 shrink-0">
                      <TrendingUp className="w-3 h-3 text-[#16a34a]" /> Top Selling
                    </button>
                    <button onClick={() => openCategoryQuickAdd("Latest Products")} className="px-2.5 py-1 rounded-xl bg-[#faf5ff] text-[#9333ea] border border-[#e9d5ff] font-semibold text-[11px] flex items-center gap-1 shrink-0">
                      <span className="text-[8px] bg-[#9333ea] text-white rounded px-0.5">NEW</span> Latest
                    </button>
                    <button onClick={() => openCategoryQuickAdd("New Offers")} className="px-2.5 py-1 rounded-xl bg-[#fffbeb] text-[#d97706] border border-[#fde68a] font-semibold text-[11px] flex items-center gap-1 shrink-0">
                      <Tag className="w-3 h-3 text-[#d97706]" /> Offers
                    </button>
                    <button onClick={() => openCategoryQuickAdd("Buy 1 Get 1 Offers")} className="px-2.5 py-1 rounded-xl bg-[#fdf2f8] text-[#db2777] border border-[#fbcfe8] font-semibold text-[11px] flex items-center gap-1 shrink-0">
                      <span className="text-[8px] bg-[#db2777] text-white rounded px-0.5">B1G1</span> B1G1
                    </button>
                  </div>
                </div>
              ) : (
                items.map((it, idx) => (
                  <div key={it.key} className="bg-white border border-gray-200 rounded-xl p-2.5 shadow-2xs space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-gray-900 line-clamp-2 break-words" title={it.productName}>{it.productName}</div>
                        <div className="text-[10px] text-gray-400 font-mono flex items-center gap-1.5 mt-0.5">
                          <span>{it.itemcode}</span>
                          <span>·</span>
                          <span>{it.unit}</span>
                          {it.mrp > it.unitCost && (
                            <span className="line-through text-gray-300">MRP ₹{it.mrp}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(idx)}
                        className="p-1 rounded-lg text-gray-400 hover:text-rose-600 active:bg-rose-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                      {/* Qty Stepper */}
                      <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                        <button
                          onClick={() => updateItemQty(idx, -1)}
                          className="w-7 h-7 grid place-items-center text-gray-600 active:bg-gray-200"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <input
                          type="number"
                          value={it.qty}
                          onChange={(e) => setItemQtyDirect(idx, e.target.value)}
                          className="w-10 h-7 text-center font-bold text-gray-900 bg-white border-x border-gray-200 text-xs focus:outline-none"
                        />
                        <button
                          onClick={() => updateItemQty(idx, 1)}
                          className="w-7 h-7 grid place-items-center text-gray-600 active:bg-gray-200"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Price & Unit Cost */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingItemIndex(idx)}
                          className="text-[10px] text-gray-500 hover:text-rose-600 underline font-mono"
                          title="Edit discount or cost"
                        >
                          @ ₹{Number(it.unitCost).toFixed(2)}
                        </button>
                        <div className="text-xs font-black text-gray-900 font-mono">
                          ₹{(Number(it.netAmount) || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Remarks & Apply Coupon Strip */}
            <div className="border-t border-gray-200 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-white flex items-center justify-between gap-2 shrink-0">
              <div className="relative flex-1 max-w-md">
                <FileText className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Remarks / Bill notes..."
                  className="w-full pl-7 sm:pl-8 pr-2.5 py-1 sm:py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:border-rose-500"
                />
              </div>

              <button
                onClick={() => setShowCouponModal(true)}
                className="bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold text-xs px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-xl shadow-2xs flex items-center gap-1 transition cursor-pointer shrink-0"
              >
                <Tag className="w-3.5 h-3.5 text-gray-500" />
                <span className="hidden xs:inline">Coupon</span>
                {appliedCoupon && (
                  <span className="bg-emerald-500 text-white text-[9px] px-1 rounded font-bold">✓</span>
                )}
              </button>
            </div>
          </div>

          {/* ─── Financial Summary Metric Strip (Desktop & Mobile) ──── */}
          
          {/* Desktop Summary Bar (>= md) */}
          <div className="hidden md:flex mt-2 bg-white border border-gray-200 rounded-2xl p-2 shadow-2xs shrink-0 items-center justify-between divide-x divide-gray-200 text-center">
            <div className="flex-1 px-1">
              <div className="text-sm font-extrabold text-gray-900 font-mono leading-tight">{items.length}</div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Items</div>
            </div>
            <div className="flex-1 px-1">
              <div className="text-sm font-extrabold text-gray-900 font-mono leading-tight">{totals.totalQty.toFixed(3)}</div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Quantity</div>
            </div>
            <div className="flex-1 px-1">
              <div className="text-sm font-extrabold text-gray-900 font-mono leading-tight">₹{totals.totalMRP.toFixed(2)}</div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">MRP Total</div>
            </div>
            <div className="flex-1 px-1">
              <div className="text-sm font-extrabold text-gray-900 font-mono leading-tight">₹{totals.taxAmount.toFixed(2)}</div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Tax Amount</div>
            </div>
            <div className="flex-1 px-1">
              <button onClick={() => setShowAddChargesModal(true)} className="text-sm font-extrabold text-gray-900 font-mono leading-tight hover:underline">
                ₹{totals.addChargesSum.toFixed(2)}
              </button>
              <div onClick={() => setShowAddChargesModal(true)} className="text-[10px] font-bold text-rose-600 hover:text-rose-700 uppercase tracking-tight cursor-pointer">
                Add Charges +
              </div>
            </div>
            <div className="flex-1 px-1">
              <div className="text-sm font-extrabold text-[#e11d48] font-mono leading-tight">₹{totals.totalDiscount.toFixed(2)}</div>
              <div className="text-[10px] font-bold text-[#e11d48] uppercase tracking-tight">Discount</div>
            </div>
            <div className="flex-1 px-1 flex flex-col items-center">
              <div className="flex items-center gap-0.5">
                <span className="text-[10px] font-bold text-gray-400">%</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={flatDiscountPct || ""}
                  onChange={(e) => setFlatDiscountPct(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                  placeholder="0.00"
                  className="w-12 text-center text-xs font-mono font-bold bg-gray-50 border border-gray-200 rounded py-0.5 focus:outline-none focus:border-rose-500"
                />
              </div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Flat Discount</div>
            </div>
            <div className="flex-1 px-1">
              <div className="text-sm font-extrabold text-gray-600 font-mono leading-tight">
                {totals.roundoff > 0 ? `+₹${totals.roundoff.toFixed(2)}` : `₹${totals.roundoff.toFixed(2)}`}
              </div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Round OFF</div>
            </div>
            <div className="px-4 py-1.5 bg-[#fff1f2] border border-[#ffe4e6] rounded-xl flex flex-col items-center justify-center min-w-[130px] ml-1">
              <div className="text-lg font-black text-[#e11d48] font-mono leading-none">
                ₹{totals.grandTotal.toFixed(2)}
              </div>
              <div className="text-[9.5px] font-bold text-gray-600 uppercase tracking-wider mt-0.5">
                Grand Total
              </div>
            </div>
          </div>

          {/* Mobile Compact Summary Card (< md) */}
          <div className="md:hidden mt-2 bg-white border border-gray-200 rounded-xl p-2 shadow-2xs shrink-0 space-y-1.5">
            <div className="flex items-center justify-between text-xs text-gray-600 px-1">
              <div>
                <span className="text-gray-400">Items: </span>
                <span className="font-bold font-mono text-gray-800">{items.length}</span>
                <span className="text-gray-300 mx-1.5">|</span>
                <span className="text-gray-400">Qty: </span>
                <span className="font-bold font-mono text-gray-800">{totals.totalQty}</span>
              </div>
              <div>
                <span className="text-gray-400">Disc: </span>
                <span className="font-bold font-mono text-[#e11d48]">−₹{totals.totalDiscount}</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddChargesModal(true)}
                  className="text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md"
                >
                  Charges +
                </button>
                <button
                  onClick={() => {
                    const pct = prompt("Enter % flat discount:", flatDiscountPct.toString());
                    if (pct !== null) setFlatDiscountPct(Math.max(0, Math.min(100, parseFloat(pct) || 0)));
                  }}
                  className="text-[10px] font-bold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md"
                >
                  {flatDiscountPct > 0 ? `${flatDiscountPct}% Disc` : "% Disc"}
                </button>
              </div>

              <div className="flex items-center gap-1.5 bg-[#fff1f2] border border-[#ffe4e6] px-3 py-1 rounded-lg">
                <span className="text-[10px] font-bold text-gray-500 uppercase">Total:</span>
                <span className="text-base font-black text-[#e11d48] font-mono leading-none">
                  ₹{totals.grandTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* ─── Bottom Tender Action Buttons (Responsive Grid) ────── */}
          
          {/* Desktop Tender Bar (>= md screens) */}
          <div className="hidden md:flex mt-2 items-center gap-1.5 shrink-0 flex-wrap">
            <button
              onClick={() => {
                if (items.length === 0) {
                  toast.warning("Please add products to the cart first", { icon: "🛒" });
                  searchInputRef.current?.focus();
                  return;
                }
                setSplitAmounts({ cash: totals.grandTotal, card: 0, upi: 0, credit: 0 });
                setShowSplitPayModal(true);
              }}
              disabled={isSubmitting}
              className="flex-1 h-10 px-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 font-semibold text-xs flex items-center justify-center gap-1.5 shadow-2xs transition cursor-pointer"
            >
              <Layers className="w-3.5 h-3.5 text-gray-500" />
              <span>Multiple Pay</span>
              <span className="font-mono text-[10px] text-gray-400">(F2)</span>
            </button>

            <button
              onClick={() => {
                if (items.length === 0) {
                  toast.warning("Please add products to the cart first", { icon: "🛒" });
                  searchInputRef.current?.focus();
                  return;
                }
                if (!selectedCustomer?.id) {
                  toast.error("Credit / Udhar sale requires selecting a customer");
                  setShowCustomerModal(true);
                  return;
                }
                executeCheckout("credit");
              }}
              disabled={isSubmitting}
              className="flex-1 h-10 px-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 font-semibold text-xs flex items-center justify-center gap-1.5 shadow-2xs transition cursor-pointer"
            >
              <Clock className="w-3.5 h-3.5 text-gray-500" />
              <span>Pay Later</span>
              <span className="font-mono text-[10px] text-gray-400">(F11)</span>
            </button>

            <button
              onClick={() => {
                if (items.length === 0) {
                  toast.warning("Please add products to the cart first", { icon: "🛒" });
                  searchInputRef.current?.focus();
                  return;
                }
                if (!selectedCustomer?.id) {
                  toast.info("Please select a customer to redeem credit/points");
                  setShowCustomerModal(true);
                  return;
                }
                setShowRedeemCreditModal(true);
              }}
              disabled={isSubmitting}
              className="flex-1 h-10 px-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 font-semibold text-xs flex items-center justify-center gap-1.5 shadow-2xs transition cursor-pointer"
            >
              <Wallet className="w-3.5 h-3.5 text-gray-500" />
              <span>Redeem Credit</span>
              {redeemedCreditAmount > 0 && (
                <span className="bg-emerald-500 text-white text-[9px] px-1 rounded font-mono">₹{redeemedCreditAmount}</span>
              )}
            </button>

            <button
              onClick={() => {
                if (items.length === 0) {
                  toast.warning("Please add products to the cart first", { icon: "🛒" });
                  searchInputRef.current?.focus();
                  return;
                }
                handleHoldBill(true);
              }}
              disabled={isSubmitting}
              className="flex-1 h-10 px-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 font-semibold text-xs flex items-center justify-center gap-1.5 shadow-2xs transition cursor-pointer"
            >
              <Printer className="w-3.5 h-3.5 text-gray-500" />
              <span>Hold & Print</span>
              <span className="font-mono text-[10px] text-gray-400">(F7)</span>
            </button>

            <button
              onClick={() => {
                if (items.length === 0) {
                  toast.warning("Please add products to the cart first", { icon: "🛒" });
                  searchInputRef.current?.focus();
                  return;
                }
                executeCheckout("upi", null, true);
              }}
              disabled={isSubmitting}
              className="flex-1 h-10 px-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 font-semibold text-xs flex items-center justify-center gap-1.5 shadow-2xs transition cursor-pointer"
            >
              <Smartphone className="w-3.5 h-3.5 text-gray-500" />
              <span>UPI & Print</span>
              <span className="font-mono text-[10px] text-gray-400">(F10)</span>
            </button>

            <button
              onClick={() => {
                if (items.length === 0) {
                  toast.warning("Please add products to the cart first", { icon: "🛒" });
                  searchInputRef.current?.focus();
                  return;
                }
                executeCheckout("card", null, true);
              }}
              disabled={isSubmitting}
              className="flex-1 h-10 px-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-gray-800 font-semibold text-xs flex items-center justify-center gap-1.5 shadow-2xs transition cursor-pointer"
            >
              <CreditCard className="w-3.5 h-3.5 text-gray-500" />
              <span>Card & Print</span>
              <span className="font-mono text-[10px] text-gray-400">(F9)</span>
            </button>

            {/* Large Green Cash & Print CTA */}
            <button
              onClick={() => {
                if (items.length === 0) {
                  toast.warning("Please add products to the cart first", { icon: "🛒" });
                  searchInputRef.current?.focus();
                  return;
                }
                setPendingPrintAfterPay(true);
                setCashTendered(totals.grandTotal.toString());
                setShowCashTenderModal(true);
              }}
              disabled={isSubmitting}
              className="h-10 px-6 rounded-xl bg-[#16a34a] hover:bg-[#15803d] active:scale-98 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-sm transition cursor-pointer min-w-[170px]"
            >
              <Printer className="w-4 h-4" />
              <span>Cash & Print</span>
              <span className="font-mono text-[10px] opacity-80">(F8)</span>
            </button>
          </div>

          {/* Mobile Bottom Action Bar (< md screens) */}
          <div className="md:hidden mt-2 space-y-1.5 shrink-0">
            {/* Primary Large Cash & Print */}
            <button
              onClick={() => {
                if (items.length === 0) {
                  toast.warning("Please add products to the cart first", { icon: "🛒" });
                  searchInputRef.current?.focus();
                  return;
                }
                setPendingPrintAfterPay(true);
                setCashTendered(totals.grandTotal.toString());
                setShowCashTenderModal(true);
              }}
              disabled={isSubmitting}
              className="w-full h-11 rounded-xl bg-[#16a34a] hover:bg-[#15803d] active:scale-98 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-sm transition"
            >
              <Printer className="w-4 h-4" />
              <span>Cash & Print (₹{totals.grandTotal})</span>
            </button>

            {/* Secondary Row: UPI, Card, More */}
            <div className="grid grid-cols-3 gap-1.5">
              <button
                onClick={() => {
                  if (items.length === 0) return toast.warning("Add products first");
                  executeCheckout("upi", null, true);
                }}
                className="h-9 rounded-lg bg-white border border-gray-200 text-gray-800 font-bold text-xs flex items-center justify-center gap-1 active:bg-gray-100"
              >
                <Smartphone className="w-3.5 h-3.5 text-sky-600" />
                <span>UPI</span>
              </button>

              <button
                onClick={() => {
                  if (items.length === 0) return toast.warning("Add products first");
                  executeCheckout("card", null, true);
                }}
                className="h-9 rounded-lg bg-white border border-gray-200 text-gray-800 font-bold text-xs flex items-center justify-center gap-1 active:bg-gray-100"
              >
                <CreditCard className="w-3.5 h-3.5 text-indigo-600" />
                <span>Card</span>
              </button>

              <button
                onClick={() => setShowMorePayModal(true)}
                className="h-9 rounded-lg bg-white border border-gray-200 text-gray-800 font-bold text-xs flex items-center justify-center gap-1 active:bg-gray-100"
              >
                <MoreHorizontal className="w-3.5 h-3.5 text-gray-500" />
                <span>More</span>
              </button>
            </div>
          </div>
        </main>

        {/* ─── 2C. RIGHT SIDEBAR PANEL (Actions, Customer, Last Bill) ─ */}
        <aside
          className={`
            w-full lg:w-72 xl:w-80 bg-[#f8fafc] border-l border-gray-200/90
            p-3 flex flex-col gap-3 shrink-0 overflow-y-auto
            ${mobileActiveTab === "actions" ? "flex" : "hidden lg:flex"}
          `}
        >
          {/* Mobile Back to Cart Button (< lg screens) */}
          <div className="lg:hidden flex items-center justify-between pb-2 border-b border-gray-200">
            <span className="text-xs font-bold text-gray-900">Actions & Customer Info</span>
            <button
              onClick={() => setMobileActiveTab("cart")}
              className="text-xs font-bold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg"
            >
              ← Back to Cart ({items.length})
            </button>
          </div>

          {/* Quick Actions Header */}
          <div>
            <div className="flex items-center justify-between pb-1.5">
              <span className="text-xs font-bold text-gray-900">Quick Actions</span>
              <button
                onClick={() => toast.info("Customise Quick Actions layout")}
                className="text-[11px] font-semibold text-gray-500 hover:text-gray-800"
              >
                Edit
              </button>
            </div>

            {/* 2x3 Grid of Action Tiles */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={clearBill}
                className="p-2.5 rounded-2xl bg-white border border-gray-200 hover:border-rose-300 hover:shadow-xs transition flex flex-col items-center justify-center text-center group cursor-pointer"
              >
                <div className="w-6 h-6 rounded-lg bg-rose-50 text-[#e11d48] grid place-items-center mb-1">
                  <FileText className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-bold text-[#e11d48]">New Bill</span>
              </button>

              <button
                onClick={() => handleHoldBill(false)}
                className="p-2.5 rounded-2xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-xs transition flex flex-col items-center justify-center text-center group cursor-pointer"
              >
                <div className="w-6 h-6 rounded-lg bg-gray-50 text-gray-700 grid place-items-center mb-1">
                  <PauseCircle className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-semibold text-gray-800">Hold Bill</span>
              </button>

              <button
                onClick={() => navigate(`${base}/orders`)}
                className="p-2.5 rounded-2xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-xs transition flex flex-col items-center justify-center text-center group cursor-pointer"
              >
                <div className="w-6 h-6 rounded-lg bg-gray-50 text-gray-700 grid place-items-center mb-1">
                  <CreditCard className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-semibold text-gray-800">Payments</span>
              </button>

              <button
                onClick={() => toast.info("Issue Credit Note / Return Voucher")}
                className="p-2.5 rounded-2xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-xs transition flex flex-col items-center justify-center text-center group cursor-pointer"
              >
                <div className="w-6 h-6 rounded-lg bg-gray-50 text-gray-700 grid place-items-center mb-1">
                  <FileText className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-semibold text-gray-800">Credit Note</span>
              </button>

              <button
                onClick={() => {
                  if (!selectedCustomer?.id) {
                    toast.info("Please select a customer first to redeem points");
                    setShowCustomerModal(true);
                    return;
                  }
                  setShowRedeemCreditModal(true);
                }}
                className="p-2.5 rounded-2xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-xs transition flex flex-col items-center justify-center text-center group cursor-pointer"
              >
                <div className="w-6 h-6 rounded-lg bg-gray-50 text-gray-700 grid place-items-center mb-1">
                  <Gift className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-semibold text-gray-800">Redeem Points</span>
              </button>

              <button
                onClick={() => setShowCashControlModal(true)}
                className="p-2.5 rounded-2xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-xs transition flex flex-col items-center justify-center text-center group cursor-pointer"
              >
                <div className="w-6 h-6 rounded-lg bg-gray-50 text-gray-700 grid place-items-center mb-1">
                  <Banknote className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-semibold text-gray-800">Cash Control</span>
              </button>
            </div>
          </div>

          {/* Customer Details Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-3.5 shadow-2xs">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
              <div className="flex items-center gap-1.5 text-xs font-bold text-gray-900">
                <User className="w-3.5 h-3.5 text-[#e11d48]" />
                <span>Customer Details</span>
              </div>
              <button
                onClick={() => setShowCustomerModal(true)}
                className="text-[11px] font-semibold text-blue-600 hover:underline cursor-pointer"
              >
                Select Customer
              </button>
            </div>

            <div className="mt-2.5 space-y-1.5 text-xs">
              <div className="flex justify-between text-gray-600">
                <span className="text-gray-500">Last Visited</span>
                <span className="text-gray-400 font-mono">:</span>
                <span className="font-semibold text-gray-800 text-right w-24">
                  {customerStats.last_visited && customerStats.last_visited !== "-"
                    ? new Date(customerStats.last_visited).toLocaleDateString("en-IN")
                    : "-"}
                </span>
              </div>

              <div className="flex justify-between text-gray-600">
                <span className="text-gray-500">Last Bill Amount</span>
                <span className="text-gray-400 font-mono">:</span>
                <span className="font-bold text-gray-900 font-mono text-right w-24">
                  ₹{customerStats.last_bill_amount || 0}
                </span>
              </div>

              <div className="flex justify-between text-gray-600">
                <span className="text-gray-500">Most Purchased Item</span>
                <span className="text-gray-400 font-mono">:</span>
                <span className="font-semibold text-gray-800 truncate text-right w-24" title={customerStats.most_purchased_item}>
                  {customerStats.most_purchased_item || "0"}
                </span>
              </div>

              <div className="flex justify-between text-gray-600">
                <span className="text-gray-500">Payment Mode</span>
                <span className="text-gray-400 font-mono">:</span>
                <span className="font-semibold uppercase text-gray-800 text-right w-24">
                  {customerStats.payment_mode || "-"}
                </span>
              </div>

              <div className="flex justify-between text-gray-600">
                <span className="text-gray-500">Due Payment</span>
                <span className="text-gray-400 font-mono">:</span>
                <span className={`font-bold font-mono text-right w-24 ${customerStats.due_payment > 0 ? "text-rose-600" : "text-gray-900"}`}>
                  ₹{customerStats.due_payment || 0}
                </span>
              </div>

              <div className="flex justify-between text-gray-600">
                <span className="text-gray-500">Total Purchase</span>
                <span className="text-gray-400 font-mono">:</span>
                <span className="font-bold text-gray-900 font-mono text-right w-24">
                  ₹{customerStats.total_purchase || 0}
                </span>
              </div>

              <div className="flex justify-between text-gray-600">
                <span className="text-gray-500">Loyalty Points</span>
                <span className="text-gray-400 font-mono">:</span>
                <span className="font-bold text-amber-600 font-mono text-right w-24">
                  {customerStats.loyalty_points || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Last Bill Card & Print Last Bill */}
          <div className="bg-white rounded-2xl border border-gray-200 p-3.5 shadow-2xs mt-auto">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-50 text-[#e11d48] grid place-items-center shrink-0">
                <Receipt className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0 space-y-0.5 text-xs">
                <div className="flex items-center justify-between text-gray-600">
                  <span className="text-gray-500">Last Bill No.</span>
                  <span className="font-bold font-mono text-gray-900">
                    {lastBill?.order_number || "ORD73"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-gray-600">
                  <span className="text-gray-500">Last Bill Amount</span>
                  <span className="font-extrabold font-mono text-gray-900">
                    ₹{lastBill ? (lastBill.pricing?.total || 0).toFixed(2) : "17.00"}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                if (lastBill) triggerInvoicePrint(lastBill);
                else toast.info("No recent bill available to print");
              }}
              className="w-full mt-3 py-2 px-3 rounded-xl border border-rose-200 text-[#e11d48] hover:bg-rose-50 active:scale-98 font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Print Last Bill</span>
            </button>
          </div>
        </aside>
      </div>

      {/* ═════════════════════════════════════════════════════════════ */}
      {/* ─── 3. BOTTOM FOOTER BAR ─────────────────────────────────── */}
      {/* ═════════════════════════════════════════════════════════════ */}
      <footer className="h-7 sm:h-8 bg-[#f8fafc] border-t border-gray-200 px-3 sm:px-4 flex items-center justify-between text-[11px] sm:text-xs text-gray-500 shrink-0 select-none">
        <div className="flex items-center gap-1.5 font-medium truncate max-w-[170px] sm:max-w-none">
          <span className="font-black text-[#e11d48]">BMKO</span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-600 font-semibold truncate">Trusted by India's Families</span>
        </div>

        <div className="hidden sm:flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-gray-600 font-medium">All Systems Operational</span>
          <span className="text-gray-400 font-mono text-[11px]">v1.0.0</span>
        </div>

        <div className="flex items-center gap-1 font-script text-sm sm:text-base text-gray-800 font-bold shrink-0">
          <span>Bigger Together Everyday</span>
          <span className="text-[#e11d48]">❤️</span>
        </div>
      </footer>

      {/* ═════════════════════════════════════════════════════════════ */}
      {/* ─── 4. MODALS & SLIDE-OVER PANELS ────────────────────────── */}
      {/* ═════════════════════════════════════════════════════════════ */}

      {/* ─── Modal: Mobile More Payment & Tender Options ──────────── */}
      <Dialog open={showMorePayModal} onOpenChange={setShowMorePayModal}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-sm font-bold">More Payment Options</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <button
              onClick={() => {
                setShowMorePayModal(false);
                setSplitAmounts({ cash: totals.grandTotal, card: 0, upi: 0, credit: 0 });
                setShowSplitPayModal(true);
              }}
              className="w-full py-2.5 px-3 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-800 font-semibold text-xs flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-gray-500" />
                <span>Multiple Pay (Split Tender)</span>
              </div>
              <span className="font-mono text-[10px] text-gray-400">F2</span>
            </button>

            <button
              onClick={() => {
                setShowMorePayModal(false);
                if (!selectedCustomer?.id) {
                  toast.error("Credit sale requires selecting a customer");
                  setShowCustomerModal(true);
                  return;
                }
                executeCheckout("credit");
              }}
              className="w-full py-2.5 px-3 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-800 font-semibold text-xs flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span>Pay Later (Credit/Udhar)</span>
              </div>
              <span className="font-mono text-[10px] text-gray-400">F11</span>
            </button>

            <button
              onClick={() => {
                setShowMorePayModal(false);
                if (!selectedCustomer?.id) {
                  toast.info("Please select a customer first");
                  setShowCustomerModal(true);
                  return;
                }
                setShowRedeemCreditModal(true);
              }}
              className="w-full py-2.5 px-3 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-800 font-semibold text-xs flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-gray-500" />
                <span>Redeem Credit / Loyalty</span>
              </div>
            </button>

            <button
              onClick={() => {
                setShowMorePayModal(false);
                handleHoldBill(true);
              }}
              className="w-full py-2.5 px-3 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-800 font-semibold text-xs flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Printer className="w-4 h-4 text-gray-500" />
                <span>Hold Bill & Print Token</span>
              </div>
              <span className="font-mono text-[10px] text-gray-400">F7</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Mobile Item Price & Discount Edit ─────────────── */}
      <Dialog open={editingItemIndex !== null} onOpenChange={(open) => { if (!open) setEditingItemIndex(null); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-xs font-bold truncate">
              {editingItemIndex !== null && items[editingItemIndex]?.productName}
            </DialogTitle>
          </DialogHeader>
          {editingItemIndex !== null && items[editingItemIndex] && (() => {
            const it = items[editingItemIndex];
            return (
              <div className="space-y-3 mt-2 text-xs">
                <div>
                  <Label className="text-[11px] font-bold text-gray-700">Unit Cost (₹)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={it.unitCost}
                    onChange={(e) => updateItemUnitCost(editingItemIndex, e.target.value)}
                    className="mt-1 text-xs font-mono font-bold"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px] font-bold text-gray-700">Flat Discount (₹)</Label>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      value={it.discountFlat}
                      onChange={(e) => updateItemDiscountFlat(editingItemIndex, e.target.value)}
                      className="mt-1 text-xs font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] font-bold text-gray-700">Discount (%)</Label>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      max="100"
                      value={it.discountPct}
                      onChange={(e) => updateItemDiscountPct(editingItemIndex, e.target.value)}
                      className="mt-1 text-xs font-mono"
                    />
                  </div>
                </div>
                <DialogFooter className="mt-2">
                  <Button size="sm" className="w-full bg-[#e11d48] hover:bg-[#be123c] text-white font-bold" onClick={() => setEditingItemIndex(null)}>
                    Done
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Quick Catalog Product Selector ────────────────── */}
      <Dialog open={showCatalogModal} onOpenChange={setShowCatalogModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-3 sm:p-4">
          <DialogHeader className="pr-10 pb-1">
            <DialogTitle className="flex items-center gap-2 text-base">
              <span className="font-bold text-gray-900">{catalogFilterTitle}</span>
              <span className="text-[11px] font-semibold bg-rose-50 text-rose-600 border border-rose-200/60 px-2 py-0.5 rounded-full">
                Tap to add
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto mt-2 min-h-60">
            {isLoadingCatalog ? (
              <div className="p-12 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin text-[#e11d48]" />
                <span>Loading catalog items...</span>
              </div>
            ) : catalogProducts.length === 0 ? (
              <div className="p-12 text-center text-xs text-gray-500">
                <p className="font-semibold text-gray-700">No products available in this section</p>
                <p className="text-[11px] text-gray-400 mt-1">Try scanning barcode or searching by product name.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-2.5">
                {catalogProducts.map((p) => {
                  const price = p.selling_price;
                  const mrp = p.mrp || price;
                  return (
                    <div
                      key={p.id || p._id}
                      onClick={() => {
                        addItemToCart(p);
                        toast.success(`Added ${p.name}`);
                      }}
                      className="p-2.5 sm:p-3 bg-white border border-gray-200 rounded-xl hover:border-rose-400 hover:shadow-xs transition cursor-pointer flex flex-col justify-between group"
                    >
                      <div>
                        <div className="text-xs font-bold text-gray-800 group-hover:text-rose-900 line-clamp-2">
                          {p.name}
                        </div>
                        <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                          {p.barcode || p.sku || "PCS"}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between pt-2 border-t border-gray-100">
                        <div>
                          {mrp && mrp > price && (
                            <span className="text-[10px] text-gray-400 line-through mr-1 font-mono">₹{mrp}</span>
                          )}
                          <span className="text-xs font-black text-rose-600 font-mono">₹{price}</span>
                        </div>
                        <span className="w-5 h-5 rounded-full bg-rose-50 text-rose-600 grid place-items-center group-hover:bg-rose-600 group-hover:text-white transition">
                          <Plus className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" size="sm" onClick={() => setShowCatalogModal(false)}>
              Close Catalog
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Quick Add / Select Customer ───────────────────── */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCustomer} className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Full Name *</Label>
              <Input
                required
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                placeholder="e.g. Ramesh Kumar"
                className="mt-1 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Phone Number *</Label>
              <Input
                required
                type="tel"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                placeholder="10-digit mobile number"
                className="mt-1 text-xs font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">Email (Optional)</Label>
              <Input
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                placeholder="customer@email.com"
                className="mt-1 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Address (Optional)</Label>
              <Input
                value={newCustomer.address}
                onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                placeholder="House / Street / Locality"
                className="mt-1 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">GSTIN (Optional)</Label>
              <Input
                value={newCustomer.gstin}
                onChange={(e) => setNewCustomer({ ...newCustomer, gstin: e.target.value })}
                placeholder="22AAAAA0000A1Z5"
                className="mt-1 text-xs font-mono uppercase"
              />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowCustomerModal(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-[#e11d48] hover:bg-[#be123c] text-white font-bold">
                Save & Select
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Cash Payment & Change Return Calculator ────────── */}
      <Dialog open={showCashTenderModal} onOpenChange={setShowCashTenderModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cash Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Bill Total:</span>
                <span className="font-extrabold text-gray-900 font-mono text-base">₹{totals.grandTotal}</span>
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold text-gray-700">Cash Received from Customer</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">₹</span>
                <Input
                  autoFocus
                  type="number"
                  step="any"
                  value={cashTendered}
                  onChange={(e) => setCashTendered(e.target.value)}
                  className="pl-8 text-base font-bold font-mono"
                />
              </div>
            </div>

            {/* Quick Note Denominations */}
            <div className="flex gap-1.5 flex-wrap">
              {[50, 100, 200, 500].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setCashTendered(d.toString())}
                  className="px-2.5 py-1 text-xs font-semibold bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-200"
                >
                  ₹{d}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCashTendered(totals.grandTotal.toString())}
                className="px-2.5 py-1 text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-800 rounded-lg border border-rose-200"
              >
                Exact (₹{totals.grandTotal})
              </button>
            </div>

            {/* Change to Return */}
            <div className="p-3 rounded-xl bg-gray-100 flex items-center justify-between text-sm font-bold">
              <span className="text-gray-600">Change to Return:</span>
              <span className={`font-mono text-base ${parseFloat(cashTendered) - totals.grandTotal >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                ₹{Math.max(0, (parseFloat(cashTendered) || 0) - totals.grandTotal).toFixed(2)}
              </span>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setShowCashTenderModal(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={isSubmitting || (parseFloat(cashTendered) || 0) < totals.grandTotal}
              onClick={() => executeCheckout("cash", null, pendingPrintAfterPay)}
              className="bg-[#16a34a] hover:bg-[#15803d] text-white font-bold"
            >
              Complete Sale {pendingPrintAfterPay ? "& Print" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Split Payment (Multiple Pay) ──────────────────── */}
      <Dialog open={showSplitPayModal} onOpenChange={setShowSplitPayModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Multiple Pay (Split Tender)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 flex justify-between text-sm">
              <span className="text-gray-600">Bill Total:</span>
              <span className="font-extrabold text-gray-900 font-mono text-base">₹{totals.grandTotal}</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cash (₹)</Label>
                <Input
                  type="number"
                  value={splitAmounts.cash}
                  onChange={(e) => setSplitAmounts({ ...splitAmounts, cash: parseFloat(e.target.value) || 0 })}
                  className="mt-1 text-xs font-mono font-bold"
                />
              </div>
              <div>
                <Label className="text-xs">Card (₹)</Label>
                <Input
                  type="number"
                  value={splitAmounts.card}
                  onChange={(e) => setSplitAmounts({ ...splitAmounts, card: parseFloat(e.target.value) || 0 })}
                  className="mt-1 text-xs font-mono font-bold"
                />
              </div>
              <div>
                <Label className="text-xs">UPI (₹)</Label>
                <Input
                  type="number"
                  value={splitAmounts.upi}
                  onChange={(e) => setSplitAmounts({ ...splitAmounts, upi: parseFloat(e.target.value) || 0 })}
                  className="mt-1 text-xs font-mono font-bold"
                />
              </div>
              <div>
                <Label className="text-xs">Credit / Udhar (₹)</Label>
                <Input
                  type="number"
                  value={splitAmounts.credit}
                  onChange={(e) => setSplitAmounts({ ...splitAmounts, credit: parseFloat(e.target.value) || 0 })}
                  className="mt-1 text-xs font-mono font-bold"
                />
              </div>
            </div>

            {(() => {
              const sum = (splitAmounts.cash || 0) + (splitAmounts.card || 0) + (splitAmounts.upi || 0) + (splitAmounts.credit || 0);
              const diff = totals.grandTotal - sum;
              return (
                <div className="p-2.5 rounded-xl bg-gray-100 flex justify-between text-xs font-semibold">
                  <span className="text-gray-600">Total Tendered: ₹{sum.toFixed(2)}</span>
                  <span className={diff === 0 ? "text-emerald-700" : "text-rose-600"}>
                    {diff === 0 ? "Balanced ✓" : diff > 0 ? `Remaining: ₹${diff.toFixed(2)}` : `Excess: ₹${Math.abs(diff).toFixed(2)}`}
                  </span>
                </div>
              );
            })()}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setShowSplitPayModal(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={
                isSubmitting ||
                Math.abs(
                  totals.grandTotal -
                  ((splitAmounts.cash || 0) + (splitAmounts.card || 0) + (splitAmounts.upi || 0) + (splitAmounts.credit || 0))
                ) > 0.05
              }
              onClick={() => executeCheckout("split", splitAmounts, false)}
              className="bg-[#e11d48] hover:bg-[#be123c] text-white font-bold"
            >
              Complete Split Sale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Additional Charges ─────────────────────────────── */}
      <Dialog open={showAddChargesModal} onOpenChange={setShowAddChargesModal}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Additional Charges</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Packing Charge (₹)</Label>
              <Input
                type="number"
                min="0"
                value={additionalCharges.packing}
                onChange={(e) => setAdditionalCharges({ ...additionalCharges, packing: parseFloat(e.target.value) || 0 })}
                className="mt-1 text-xs font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">Delivery Charge (₹)</Label>
              <Input
                type="number"
                min="0"
                value={additionalCharges.delivery}
                onChange={(e) => setAdditionalCharges({ ...additionalCharges, delivery: parseFloat(e.target.value) || 0 })}
                className="mt-1 text-xs font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">Service / Other Charge (₹)</Label>
              <Input
                type="number"
                min="0"
                value={additionalCharges.service}
                onChange={(e) => setAdditionalCharges({ ...additionalCharges, service: parseFloat(e.target.value) || 0 })}
                className="mt-1 text-xs font-mono"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button size="sm" className="w-full bg-[#e11d48] hover:bg-[#be123c] text-white font-bold" onClick={() => setShowAddChargesModal(false)}>
              Apply Charges
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Apply Coupon ──────────────────────────────────── */}
      <Dialog open={showCouponModal} onOpenChange={setShowCouponModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span>Apply Coupon & Promo Code</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold text-gray-700">Enter Coupon Code</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="e.g. FESTIVE10, SAVE50"
                  className="text-xs font-mono font-bold uppercase"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    if (!couponCode.trim()) {
                      toast.error("Please enter a coupon code");
                      return;
                    }
                    const code = couponCode.trim().toUpperCase();
                    let discount = 50;
                    if (code.includes("100")) discount = 100;
                    else if (code.includes("20")) discount = 20;
                    else if (code.includes("10")) discount = Math.round(totals.grandTotal * 0.1);
                    setAppliedCoupon({ code, discount_amount: discount });
                    toast.success(`Coupon ${code} applied (-₹${discount})`);
                    setShowCouponModal(false);
                  }}
                  className="bg-[#e11d48] hover:bg-[#be123c] text-white font-bold shrink-0"
                >
                  Apply Code
                </Button>
              </div>
            </div>

            <div>
              <div className="text-xs font-bold text-gray-600 mb-2">Available Store Coupons:</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { code: "SAVE10", desc: "10% Instant Discount", getDisc: (gt) => Math.round(gt * 0.1) },
                  { code: "WELCOME50", desc: "Flat ₹50 OFF", getDisc: () => 50 },
                  { code: "FESTIVE100", desc: "Flat ₹100 OFF on ₹500+", getDisc: () => 100 },
                  { code: "BABU20", desc: "Flat ₹20 OFF", getDisc: () => 20 },
                ].map((cp) => (
                  <div
                    key={cp.code}
                    className="p-2.5 rounded-xl border border-dashed border-amber-300 bg-amber-50/50 hover:bg-amber-50 transition cursor-pointer flex flex-col justify-between"
                    onClick={() => {
                      const disc = cp.getDisc(totals.grandTotal);
                      setAppliedCoupon({ code: cp.code, discount_amount: disc });
                      setCouponCode(cp.code);
                      toast.success(`Coupon ${cp.code} applied (-₹${disc})`);
                      setShowCouponModal(false);
                    }}
                  >
                    <div>
                      <span className="text-xs font-black font-mono text-amber-900 bg-amber-200/80 px-1.5 py-0.5 rounded">
                        {cp.code}
                      </span>
                      <p className="text-[11px] text-gray-600 font-medium mt-1">{cp.desc}</p>
                    </div>
                    <span className="text-[10px] font-bold text-emerald-700 mt-2 self-end">Apply →</span>
                  </div>
                ))}
              </div>
            </div>

            {appliedCoupon && (
              <div className="p-2.5 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between text-xs">
                <div>
                  <span className="font-bold text-emerald-900">Applied: {appliedCoupon.code}</span>
                  <span className="text-emerald-700 font-mono ml-2">(−₹{appliedCoupon.discount_amount})</span>
                </div>
                <Button
                  size="xs"
                  variant="ghost"
                  className="text-rose-600 hover:bg-rose-50 h-6 px-2 text-xs"
                  onClick={() => {
                    setAppliedCoupon(null);
                    setCouponCode("");
                    toast.info("Coupon removed");
                  }}
                >
                  Remove
                </Button>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setShowCouponModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Redeem Credit & Loyalty Points ─────────────────── */}
      <Dialog open={showRedeemCreditModal} onOpenChange={setShowRedeemCreditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-600" />
              <span>Redeem Customer Credit & Points</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500 font-medium">Customer:</span>
                <span className="font-bold text-gray-900">{selectedCustomer?.name || "Walk-in"} ({selectedCustomer?.phone || "No phone"})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 font-medium">Loyalty Points:</span>
                <span className="font-bold font-mono text-emerald-700">{customerStats.loyalty_points || 0} Pts (₹{customerStats.loyalty_points || 0})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 font-medium">Customer Due / Outstanding:</span>
                <span className={`font-bold font-mono ${customerStats.due_payment > 0 ? "text-rose-600" : "text-gray-700"}`}>
                  ₹{customerStats.due_payment || 0}
                </span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-1.5">
                <span className="text-gray-500 font-medium">Current Bill Total:</span>
                <span className="font-extrabold font-mono text-gray-900 text-sm">₹{totals.grandTotal}</span>
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold text-gray-700">Amount to Redeem from Credit / Balance (₹)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-2.5 text-gray-400 font-bold">₹</span>
                <Input
                  type="number"
                  min="0"
                  max={totals.grandTotal}
                  value={redeemedCreditAmount || ""}
                  onChange={(e) => setRedeemedCreditAmount(Math.max(0, Math.min(totals.grandTotal, parseFloat(e.target.value) || 0)))}
                  placeholder="Enter amount to redeem"
                  className="pl-8 text-sm font-mono font-bold"
                />
              </div>
            </div>

            <div className="flex gap-2">
              {[50, 100, 200, 500].map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setRedeemedCreditAmount(Math.min(totals.grandTotal, amt))}
                  className="px-2.5 py-1 text-xs font-semibold bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-200"
                >
                  ₹{amt}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setRedeemedCreditAmount(totals.grandTotal)}
                className="px-2.5 py-1 text-xs font-semibold bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-lg border border-emerald-200"
              >
                Max (₹{totals.grandTotal})
              </button>
            </div>
          </div>
          <DialogFooter className="mt-4 flex justify-between">
            {redeemedCreditAmount > 0 ? (
              <Button
                variant="outline"
                size="sm"
                className="text-rose-600 hover:bg-rose-50"
                onClick={() => {
                  setRedeemedCreditAmount(0);
                  toast.info("Redeemed credit cleared");
                  setShowRedeemCreditModal(false);
                }}
              >
                Clear Credit
              </Button>
            ) : <div />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowRedeemCreditModal(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (redeemedCreditAmount <= 0) {
                    toast.error("Please enter a valid credit amount to redeem");
                    return;
                  }
                  toast.success(`₹${redeemedCreditAmount} credit applied to bill`);
                  setShowRedeemCreditModal(false);
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              >
                Apply Credit
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Cash Control & Till Drawer ─────────────────────── */}
      <Dialog open={showCashControlModal} onOpenChange={setShowCashControlModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cash Control & Till</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {activeSession ? (
              <div className="space-y-3">
                <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-2 text-emerald-800 font-bold text-xs">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Active Cash Drawer Session Open</span>
                  </div>
                  <div className="mt-2 text-xs text-gray-700 space-y-1">
                    <div>Opening Cash: <span className="font-bold font-mono">₹{activeSession.opening_amount}</span></div>
                    <div>Opened At: <span className="font-mono">{new Date(activeSession.opened_at).toLocaleString("en-IN")}</span></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold text-gray-700">Counted Closing Cash (₹) *</Label>
                  <Input
                    type="number"
                    step="any"
                    value={closingAmount}
                    onChange={(e) => setClosingAmount(e.target.value)}
                    placeholder="Enter physical cash in drawer"
                    className="text-sm font-mono font-bold"
                  />
                  <Input
                    value={closingNote}
                    onChange={(e) => setClosingNote(e.target.value)}
                    placeholder="Closing notes / handover details (optional)"
                    className="text-xs mt-1"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={handleViewZReport}
                  >
                    View Z-Report
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="flex-1 bg-rose-600 hover:bg-rose-700 text-white font-bold"
                    onClick={handleCloseCashSession}
                  >
                    Close Till & Day End
                  </Button>
                </div>

                {zReportData && (
                  <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 text-xs font-mono space-y-1 mt-3 max-h-48 overflow-y-auto">
                    <div className="font-bold text-gray-900 border-b pb-1">Z-Report Summary</div>
                    <div>Opening: ₹{zReportData.opening_amount}</div>
                    <div>Cash Sales: ₹{zReportData.cash_sales_amount}</div>
                    <div>Expected in Till: ₹{zReportData.expected_amount}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-gray-600">
                  No cash drawer session is currently open for your account. Open a session to start recording drawer cash.
                </p>
                <div>
                  <Label className="text-xs font-bold text-gray-700">Opening Cash Amount (₹) *</Label>
                  <Input
                    autoFocus
                    type="number"
                    step="any"
                    value={openingAmount}
                    onChange={(e) => setOpeningAmount(e.target.value)}
                    placeholder="e.g. 1000"
                    className="mt-1 text-sm font-mono font-bold"
                  />
                </div>
                <Button
                  size="sm"
                  className="w-full bg-[#e11d48] hover:bg-[#be123c] text-white font-bold"
                  onClick={handleOpenCashSession}
                >
                  Open Cash Drawer
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Sheet: Held Bills Drawer ─────────────────────────────── */}
      <Sheet open={showHeldBillsSheet} onOpenChange={setShowHeldBillsSheet}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between text-base">
              <span>Saved / Held Bills ({heldBills.length})</span>
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3 overflow-y-auto max-h-[85vh]">
            {heldBills.length === 0 ? (
              <div className="py-16 text-center text-gray-400">
                <PauseCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-semibold text-gray-600">No held bills</p>
                <p className="text-xs text-gray-400 mt-0.5">Use Hold Bill (F7) during billing to park a customer cart.</p>
              </div>
            ) : (
              heldBills.map((h) => (
                <div key={h.id || h._id} className="bg-white border border-gray-200 p-3 rounded-2xl shadow-2xs space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-gray-900">{h.hold_name || "Held Bill"}</span>
                    <span className="text-[10px] text-gray-400 font-mono">
                      {new Date(h.held_at || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 flex justify-between">
                    <span>Items: {(h.items || []).length}</span>
                    <span className="font-bold font-mono text-emerald-700">₹{h.totals?.grandTotal || 0}</span>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      className="flex-1 bg-[#e11d48] hover:bg-[#be123c] text-white text-xs h-8"
                      onClick={() => handleRestoreHeldBill(h)}
                    >
                      Restore to Counter
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-rose-600 hover:bg-rose-50 text-xs h-8 px-2"
                      onClick={() => {
                        api.delete(`/hold-bills/${h.id || h._id}`).then(fetchHeldBills);
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* ─── Modal: Offline POS & Sync Hub ─────────────────────────────── */}
      <Dialog open={showSyncHubModal} onOpenChange={setShowSyncHubModal}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg text-gray-900">
              <div className="w-8 h-8 rounded-xl bg-rose-50 grid place-items-center text-[#e11d48]">
                <Database className="w-4 h-4" />
              </div>
              <span>Offline POS & Cloud Sync Hub</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Status overview cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                <span className="text-[10px] font-bold uppercase text-gray-500">Connection</span>
                <div className="flex items-center gap-1.5 mt-1 font-bold text-sm">
                  {isOnline ? (
                    <>
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-emerald-700">Online</span>
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 rounded-full bg-rose-500" />
                      <span className="text-rose-700">Offline</span>
                    </>
                  )}
                </div>
              </div>

              <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                <span className="text-[10px] font-bold uppercase text-gray-500">Pending Bills</span>
                <div className="mt-1 font-bold text-sm font-mono text-gray-900">
                  {syncStats.queued} bills
                  {syncStats.pendingRevenue > 0 && (
                    <span className="text-xs text-rose-600 ml-1">(₹{syncStats.pendingRevenue})</span>
                  )}
                </div>
              </div>

              <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl col-span-2 sm:col-span-1">
                <span className="text-[10px] font-bold uppercase text-gray-500">Cached Catalog</span>
                <div className="mt-1 font-bold text-sm text-gray-900 truncate">
                  {cachedCatalogMeta.count} Products
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap sm:flex-nowrap gap-2">
              <Button
                type="button"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold h-9"
                disabled={!isOnline || isSyncing || syncStats.queued === 0}
                onClick={() => handleSyncNow(false)}
              >
                {isSyncing ? (
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Wifi className="w-3.5 h-3.5 mr-1.5" />
                )}
                {isSyncing ? "Syncing Batch..." : `Sync Now (${syncStats.queued} Pending)`}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="flex-1 border-gray-200 text-gray-800 text-xs font-semibold h-9"
                disabled={!isOnline || isPreCaching}
                onClick={handlePreCacheCatalog}
              >
                {isPreCaching ? (
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin text-[#e11d48]" />
                ) : (
                  <Database className="w-3.5 h-3.5 mr-1.5 text-[#e11d48]" />
                )}
                {isPreCaching ? "Downloading..." : "Pre-cache Catalog"}
              </Button>
            </div>

            {/* Device & sync metadata */}
            <div className="text-[11px] text-gray-500 bg-[#f8fafc] border border-gray-200 p-2.5 rounded-xl space-y-1">
              <div className="flex justify-between">
                <span>Terminal Device ID:</span>
                <span className="font-mono font-semibold text-gray-700">{getDeviceId()}</span>
              </div>
              <div className="flex justify-between">
                <span>Last Catalog Sync:</span>
                <span className="font-semibold text-gray-700">
                  {cachedCatalogMeta.lastSync
                    ? new Date(cachedCatalogMeta.lastSync).toLocaleString("en-IN")
                    : "Never"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total Applied / Synced:</span>
                <span className="font-mono font-semibold text-emerald-600">{syncStats.applied} orders</span>
              </div>
            </div>

            {/* Recent Offline Bills Table */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Offline Bills ({offlineOrdersList.length})
                </span>
                <button
                  type="button"
                  onClick={refreshSyncStats}
                  className="text-xs text-rose-600 hover:underline flex items-center gap-1 font-semibold"
                >
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>

              {offlineOrdersList.length === 0 ? (
                <div className="text-center py-8 text-gray-400 border border-dashed border-gray-200 rounded-xl">
                  <Database className="w-6 h-6 mx-auto mb-1.5 text-gray-300" />
                  <p className="text-xs font-semibold text-gray-600">No offline bills recorded</p>
                  <p className="text-[10px] text-gray-400">Bills created when internet is disconnected will appear here.</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100 max-h-56 overflow-y-auto">
                  {offlineOrdersList.map((row) => (
                    <div key={row.offline_order_number} className="p-2.5 flex items-center justify-between hover:bg-gray-50 text-xs">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-bold text-gray-900">{row.offline_order_number}</span>
                          <span
                            className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase ${
                              row.synced
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {row.synced ? "Synced" : "Pending"}
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {new Date(row.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} •{" "}
                          {row.order?.customer?.name || "Walk-in"} •{" "}
                          {(row.order?.items || []).length} items
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900">
                          ₹{row.order?.pricing?.total || 0}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[11px] text-gray-700 hover:bg-gray-100"
                          onClick={() => {
                            if (row.order) triggerInvoicePrint(row.order);
                            else toast.error("Receipt data not found");
                          }}
                        >
                          <Printer className="w-3 h-3 mr-1 text-[#e11d48]" />
                          Print
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowSyncHubModal(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal: Camera Barcode Scanner ────────────────────────── */}
      <BarcodeScannerModal
        open={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        onScanSuccess={handleBarcodeScanned}
      />
    </div>
  );
}

// Helper ClipboardList icon
function ClipboardListIcon(props) {
  return (
    <svg
      {...props}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M12 11h4" />
      <path d="M12 16h4" />
      <path d="M8 11h.01" />
      <path d="M8 16h.01" />
    </svg>
  );
}
